/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useMemo, useRef, use } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../index.css";
import {
    Box, Typography,
} from "@mui/material";
import { ToastContainer, toast } from 'react-toastify';
import { keyframes } from '@emotion/react';
import { getPlantList, total_grid_consume, total_solar_generated, } from "../api/plant";
import { Menu, MenuItem, IconButton } from '@mui/material';
import { getUserDetails } from "../api/user";
import moment from "moment";

//weather icons and bg
import bg1 from '../image/bg1.jpg'
import HomeIcon from '@mui/icons-material/Home';
import BoltIcon from '@mui/icons-material/Bolt';
import PowerIcon from '@mui/icons-material/Power';
import NatureIcon from '@mui/icons-material/Nature';
import PaidIcon from '@mui/icons-material/Paid';
import ClearIcon from '@mui/icons-material/Clear';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import ExpandCircleDownIcon from '@mui/icons-material/ExpandCircleDown';
import ForestIcon from '@mui/icons-material/Forest';
import ArticleIcon from '@mui/icons-material/Article';

// State IDs in order for rotation
const STATE_ORDER = [
    "MY09", "MY02", "MY03", "MY08", "MY07",
    "MY10", "MY05", "MY04", "MY01", "MY06",
    "MY11", "MY12", "MY13",
];

const Map = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [activeState, setActiveState] = useState(null);
    const [statePositions, setStatePositions] = useState({});
    const [stateCenters, setStateCenters] = useState({});
    const containerRef = useRef(null);
    const intervalRef = useRef(null);
    const svgRef = useRef(null);
    const [isPositionsReady, setIsPositionsReady] = useState(false);
    const [isAutoRotationPaused, setIsAutoRotationPaused] = useState(false);
    const [plantData, setPlantData] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAutoLoopActive, setIsAutoLoopActive] = useState(false);
    const [visitedStates, setVisitedStates] = useState(new Set());
    const [loopCompleted, setLoopCompleted] = useState(false);
    const [showAutoControl, setShowAutoControl] = useState(false);
    const [autoPlaySpeed, setAutoPlaySpeed] = useState(30); // seconds per state
    const [controlPanelTimer, setControlPanelTimer] = useState(null);
    const autoPlayButtonRef = useRef(null);
    const controlPanelRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [userDetail, setUserDetail] = useState(null);
    const [totalGrid, setTotalGrid] = useState(null);
    const [totalSolarGenerated, setTotalSolarGenerated] = useState(null);
    const [totalPlantCount, setTotalPlantCount] = useState(0);
    // Extract the actual data array from plantData
    const clientsArray = plantData.data || [];

    //fetch plant data
    useEffect(() => {
        const fetchPlantData = async () => {
            try {
                setLoading(true);
                const data = await getPlantList();

                if (data?.data && Array.isArray(data.data)) {
                    // Apply your filter criteria
                    const filteredClients = data.data.filter(client =>
                        client.access_level === 1 &&
                        (client.user_type === 0 || client.user_type === 1)
                    );

                    // Create a new object with filtered data
                    const filteredData = {
                        ...data,
                        data: filteredClients
                    };

                    setPlantData(filteredData);

                    // Calculate total plant count
                    setTotalPlantCount(filteredClients.length);
                } else {
                    setPlantData(data);
                    setTotalPlantCount(0);
                }

                setError(null);
            } catch (err) {
                console.error('Failed to fetch plant data:', err);
                setError(err.message);
                setTotalPlantCount(0);
            } finally {
                setLoading(false);
            }
        };

        fetchPlantData();
    }, []);


    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // 1. First get user details
                const userData = await getUserDetails();
                setUserDetail(userData);

                // 2. Then use client_id to fetch grid data
                if (userData && userData.client_id) {
                    const formattedDate = moment().format("YYYY-MM");
                    const gridData = await total_grid_consume(userData.client_id, formattedDate);
                    setTotalGrid(gridData);

                    const solarData = await total_solar_generated(userData.client_id, formattedDate);
                    setTotalSolarGenerated(solarData);

                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);


    const STATE_CODE_TO_NAME = {
        MY01: "Johor",
        MY02: "Kedah",
        MY03: "Kelantan",
        MY04: "Melaka",
        MY05: "Negeri Sembilan",
        MY06: "Pahang",
        MY07: "Pulau Pinang",
        MY08: "Perak",
        MY09: "Perlis",
        MY10: "Selangor",
        MY11: "Terengganu",
        MY12: "Sabah",
        MY13: "Sarawak",
    };

    const STATE_NAME_TO_CODE = {
        ...Object.fromEntries(
            Object.entries(STATE_CODE_TO_NAME).map(([code, name]) => [name, code])
        ),
        "Wilayah Persekutuan Kuala Lumpur": "MY10",
        "Wilayah Persekutuan Putrajaya": "MY10",
        "Kuala Lumpur": "MY10",
    };


    const customerByState = useMemo(() => {
        const result = {
            MY01: 0,
            MY02: 0,
            MY03: 0,
            MY04: 0,
            MY05: 0,
            MY06: 0,
            MY07: 0,
            MY08: 0,
            MY09: 0,
            MY10: 0,
            MY11: 0,
            MY12: 0,
            MY13: 0,
        };

        if (!plantData?.data) return result;

        plantData.data.forEach((plant) => {
            const stateCode = STATE_NAME_TO_CODE[plant.state];
            if (stateCode) {
                result[stateCode] += 1;
            }
        });

        return result;
    }, [plantData]);

    // Initialize auto-rotation
    useEffect(() => {
        if (!isAutoLoopActive) {
            // Clear interval if auto-loop is deactivated
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        // Clear any existing interval first
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        // Find first state with users > 0
        const firstStateWithUsers = STATE_ORDER.find(state => {
            const count = customerByState[state];
            return count !== undefined && count > 0;
        });

        if (firstStateWithUsers) {
            setActiveState(firstStateWithUsers);
            setVisitedStates(new Set([firstStateWithUsers]));
        } else {
            setActiveState(null);
            return;
        }

        intervalRef.current = setInterval(() => {
            if (!isAutoRotationPaused) {
                setActiveState(prevState => {
                    if (!prevState) {
                        const firstValidState = STATE_ORDER.find(state => {
                            const count = customerByState[state];
                            return count !== undefined && count > 0;
                        });

                        setVisitedStates(prev => new Set([...prev, firstValidState]));
                        return firstValidState || null;
                    }

                    const currentIndex = STATE_ORDER.indexOf(prevState);
                    let nextIndex = (currentIndex + 1) % STATE_ORDER.length;

                    let attempts = 0;
                    while (attempts < STATE_ORDER.length) {
                        const nextState = STATE_ORDER[nextIndex];
                        const count = customerByState[nextState];

                        if (count !== undefined && count > 0) {

                            setVisitedStates(prevVisited => {
                                const updatedVisited = new Set(prevVisited);
                                updatedVisited.add(nextState);

                                // Check if we've visited all valid states
                                const allValidStates = STATE_ORDER.filter(state => {
                                    const count = customerByState[state];
                                    return count !== undefined && count > 0;
                                });

                                if (updatedVisited.size >= allValidStates.length) {
                                    setLoopCompleted(true);
                                }

                                return updatedVisited;
                            });

                            return nextState;
                        }

                        nextIndex = (nextIndex + 1) % STATE_ORDER.length;
                        attempts++;
                    }

                    return null;
                });
            }
        }, autoPlaySpeed * 1000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isAutoLoopActive, isAutoRotationPaused, autoPlaySpeed, customerByState]);


    // Map.jsx - Update the useEffect that handles navigation when loop completes:
    useEffect(() => {
        if (loopCompleted && isAutoLoopActive) {

            // Clear the current interval
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }

            // Get visited clients
            const visitedClientIds = Array.from(visitedStates || new Set());
            const visitedClients = clientsArray.filter(client =>
                client.access_level === 1 && visitedClientIds.includes(client.client_id)
            );

            // Fallback to all eligible clients if none were visited
            const clientsToShow = visitedClients.length > 0 ? visitedClients :
                clientsArray.filter(client => client.access_level === 1);


            if (clientsToShow.length > 0) {
                const timer = setTimeout(() => {
                    navigate("/BigScreen", {
                        state: {
                            autoLoopMode: true,
                            allClients: clientsToShow,
                            currentIndex: 0,
                            autoPlaySpeed: autoPlaySpeed,
                            fromMap: true,
                            cycleCount: 1  // Start counting cycles
                        }
                    });

                    // Reset Map states for when we return
                    setIsAutoLoopActive(false);
                    setLoopCompleted(false);
                    setVisitedStates(new Set());
                    setActiveState(null);

                }, autoPlaySpeed * 1000); // Wait before navigating

                return () => clearTimeout(timer);
            } else {
                console.warn("⚠️ No clients to show, stopping auto-loop");
                setIsAutoLoopActive(false);
            }
        }
    }, [loopCompleted, isAutoLoopActive, navigate, autoPlaySpeed, clientsArray, visitedStates]);
    // Map.jsx - Update this useEffect:
    useEffect(() => {
        if (location.state?.continueLoop) {

            // Get all necessary data from location state
            const speed = location.state.autoPlaySpeed || autoPlaySpeed;
            const clients = location.state.allClients || [];
            const cycleCount = location.state.cycleCount || 1;

            // Set the speed
            setAutoPlaySpeed(speed);

            // Reset all loop states first
            setIsAutoLoopActive(false);
            setLoopCompleted(false);
            setVisitedStates(new Set());

            // Clear any existing interval
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }

            // Wait a bit then start the auto-loop again
            const timer = setTimeout(() => {

                setIsAutoLoopActive(true);
                setLoopCompleted(false);
                setVisitedStates(new Set());
            }, 200);

            return () => clearTimeout(timer);
        }
    }, [location, autoPlaySpeed]);

    // Calculate state positions when component mounts
    useEffect(() => {
        const calculatePositions = () => {
            const positions = {};

            STATE_ORDER.forEach(stateId => {
                const svgElement = document.getElementById(stateId);
                if (svgElement && containerRef.current) {
                    const svgRect = svgElement.getBoundingClientRect();
                    const containerRect = containerRef.current.getBoundingClientRect();

                    // Get the center of the state
                    const bbox = svgElement.getBBox();
                    const x = svgRect.left + (bbox.width / 2) - containerRect.left;
                    const y = svgRect.top + (bbox.height / 2) - containerRect.top;

                    positions[stateId] = { x, y };
                }
            });

            setStatePositions(positions);
        };

        // Calculate on mount and window resize
        calculatePositions();
        window.addEventListener('resize', calculatePositions);

        return () => window.removeEventListener('resize', calculatePositions);
    }, []);


    // Calculate state centers for markers
    useEffect(() => {
        const allStateIds = [
            "MY01", "MY02", "MY03", "MY04", "MY05", "MY06",
            "MY07", "MY08", "MY09", "MY10", "MY11",
            "MY12", "MY13",
        ];

        const offsets = {
            MY12: { x: -20, y: 20 }, // Sabah
            MY13: { x: 45, y: 20 }, // Sarawak
            MY05: { x: 2, y: 5 },   // Negeri Sembilan
            MY11: { x: 0, y: -10 }, // Terengganu
            MY01: { x: 0, y: 5 }, // Johor
            MY04: { x: 0, y: -1 }, // Melaka
            MY01: { x: 0, y: 3 }, // Johor
            MY06: { x: 5, y: 0 }, // Pahang
            MY03: { x: 5, y: 10 }, // Kelantan
            MY02: { x: 12, y: -10 }, // Kedah
            MY09: { x: 0, y: 0 }, // Perlis
            MY07: { x: 0, y: 0 }, // Pulau Pinang
            MY08: { x: 0, y: 0 }, // Perak
            MY10: { x: 10, y: 0 }, // Selangor
            // Add more custom adjustments here
        };

        const centers = {};

        allStateIds.forEach(id => {
            const el = document.getElementById(id);
            if (!el) return;

            const bbox = el.getBBox();
            let x = bbox.x + bbox.width / 2;
            let y = bbox.y + bbox.height / 2;

            if (offsets[id]) {
                x += offsets[id].x;
                y += offsets[id].y;
            }

            centers[id] = { x, y };
        });

        setStateCenters(centers);
    }, []);

    // Helper function to get state name from ID
    const getStateName = (stateId) =>
        STATE_CODE_TO_NAME[stateId] || stateId;

    // Get popup position based on state
    const STATE_POPUP_POSITIONS = {
        // West Malaysia
        "MY09": { x: 40, y: 0 }, // Perlis
        "MY02": { x: 40, y: -120 },  // Kedah
        "MY03": { x: 50, y: -100 },   // Kelantan
        "MY08": { x: 50, y: -80 },   // Perak
        "MY07": { x: 40, y: -60 },   // Pulau Pinang
        "MY10": { x: 40, y: -100 },    // Selangor
        "MY05": { x: 40, y: -80 },    // Negeri Sembilan
        "MY04": { x: 40, y: -60 },     // Melaka
        "MY01": { x: 40, y: -40 },    // Johor
        "MY06": { x: 80, y: -80 },    // Pahang
        "MY11": { x: 60, y: -100 },   // Terengganu
        "MY12": { x: -400, y: -80 },  // Sabah
        "MY13": { x: -250, y: -100 }, // Sarawak

    };

    // Customers data
    const customers = useMemo(() => {
        if (!plantData?.data) return {};

        return plantData.data.reduce((acc, plant) => {
            const stateCode = STATE_NAME_TO_CODE[plant.state];
            if (!stateCode) return acc;

            if (!acc[stateCode]) acc[stateCode] = [];

            acc[stateCode].push({
                name: plant.company_name,
                consumption: plant.solar_system_size
                    ? `${plant.solar_system_size} kWp`
                    : "N/A",

                // Check actual values instead of userType
                hasSolar: plant.solar_system_size !== null,
                hasBess: Boolean(plant.battery_capacity_size) && plant.battery_capacity_size > 0,

                raw: plant, // optional, keep full API object
            });

            return acc;
        }, {});
    }, [plantData]);

    // Then update your getPopupPosition function:

    const getPopupPosition = (stateId) => {
        if (!statePositions[stateId] || !containerRef.current || !svgRef.current) return { x: 0, y: 0 };

        const statePos = statePositions[stateId];
        const containerRect = containerRef.current.getBoundingClientRect();
        const svgRect = svgRef.current.getBoundingClientRect();

        const offset = STATE_POPUP_POSITIONS[stateId] || { x: -100, y: -100 };

        const scaleX = svgRect.width / 1000;
        const scaleY = svgRect.height / 332;

        const screenX = svgRect.left + ((statePos.svgX + offset.x) * scaleX);
        const screenY = svgRect.top + ((statePos.svgY + offset.y) * scaleY);

        let x = screenX - containerRect.left;
        let y = screenY - containerRect.top;

        // Use % of container size instead of fixed px
        const popupWidth = containerRect.width * 0.235;   // matches your 23.5vw popup width
        const popupHeight = containerRect.height * 0.60;  // matches your 60vh popup maxHeight
        const margin = containerRect.width * 0.01;        // 1% of container as margin (replaces 20px)

        if (x < margin) x = margin;
        if (x + popupWidth > containerRect.width - margin) {
            x = containerRect.width - popupWidth - margin;
        }
        if (y < margin) {
            y = margin;
        }
        if (y + popupHeight > containerRect.height - margin) {
            y = containerRect.height - popupHeight - margin;
        }

        return { x: Math.round(x), y: Math.round(y) };
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            // If click is not on a marker and not on the popup, close popup
            if (!e.target.closest('text') &&
                !e.target.closest('[data-popup="true"]')) {
                setActiveState(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Handle click outside to close control panel
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if control panel is open
            if (!showAutoControl) return;

            // Check if click is inside the control panel
            if (controlPanelRef.current &&
                controlPanelRef.current.contains(event.target)) {
                return; // Click is inside, do nothing
            }

            // Check if click is on the PlayCircleIcon button itself
            if (autoPlayButtonRef.current &&
                autoPlayButtonRef.current.contains(event.target)) {
                return; // Click is on the button itself, let button handler handle it
            }

            // Click is outside, close the panel
            setShowAutoControl(false);

            // Clear any timer
            if (controlPanelTimer) {
                clearTimeout(controlPanelTimer);
                setControlPanelTimer(null);
            }
        };

        // Add event listener
        document.addEventListener('mousedown', handleClickOutside);

        // Cleanup
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAutoControl, controlPanelTimer]);

    // Update the useEffect that calculates statePositions:
    useEffect(() => {
        const calculatePositions = () => {
            const positions = {};

            STATE_ORDER.forEach(stateId => {
                const svgElement = document.getElementById(stateId);

                if (svgElement && svgRef.current) {
                    // Get the bounding box of the state in SVG coordinates
                    const bbox = svgElement.getBBox();

                    // Calculate center in SVG coordinates (no offsets needed)
                    const centerX = bbox.x + bbox.width / 2;
                    const centerY = bbox.y + bbox.height / 2;

                    positions[stateId] = {
                        svgX: centerX,
                        svgY: centerY,
                        bbox: bbox
                    };
                }
            });
            setStatePositions(positions);
            setIsPositionsReady(true);
        };

        // Wait a bit for SVG to render
        const timer = setTimeout(() => {
            calculatePositions();
        }, 1000);

        window.addEventListener('resize', calculatePositions);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', calculatePositions);
        };
    }, []);

    // Colors for metrics
    const colors = {
        f1: 'rgba(255, 255, 255, 0.2)',
        f2: '#ffffff',
        d4: 'rgba(96, 166, 194, 1)'
    };

    const formatNumber = (num, decimals = 0) => {
        return Number(num || 0).toLocaleString(undefined, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        });
    };

    // Mock data for the metrics
    const metrics = [
        {
            title: 'Total Plant',
            value: formatNumber(totalPlantCount),
            unit: '',
            icon: <HomeIcon sx={{ fontSize: "2vw", color: colors.f2, mb: 1 }} />,
        },
        {
            title: 'Energy Consumption',
            value: formatNumber(totalGrid?.message || totalGrid?.data?.message),
            unit: 'kWh',
            icon: <PowerIcon sx={{ fontSize: "2vw", color: colors.f2, mb: 1 }} />,
        },
        {
            title: 'Energy Generation',
            value: formatNumber(totalSolarGenerated?.message || totalSolarGenerated?.data?.message),
            unit: 'kWh',
            icon: <BoltIcon sx={{ fontSize: "2vw", color: colors.f2, mb: 1 }} />,
        },
        {
            title: 'CO2 Reduction',
            value: formatNumber(
                totalSolarGenerated?.CO2_reduction ??
                totalSolarGenerated?.data?.CO2_reduction,
                2
            ),
            unit: 'tonnes',
            icon: <NatureIcon sx={{ fontSize: "2vw", color: colors.f2, mb: 1 }} />,
        },
        {
            title: 'Equivalent Trees',
            value: formatNumber(
                totalSolarGenerated?.equivalent_trees ??
                totalSolarGenerated?.data?.equivalent_trees,
                2
            ),
            unit: 'trees',
            icon: <ForestIcon sx={{ fontSize: "2vw", color: colors.f2, mb: 1 }} />,
        },
        {
            title: 'REC',
            value: formatNumber(
                totalSolarGenerated?.REC ??
                totalSolarGenerated?.data?.REC,
                2
            ),
            unit: '',
            icon: <ArticleIcon sx={{ fontSize: "2vw", color: colors.f2, mb: 1 }} />,
        },
    ];

    // Malaysia Map Component
    const MalaysiaMap = () => (
        <svg
            stroke="#73a5b8ff"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth=".5"
            viewBox="0 0 1000 332"
            width="85%"
            height="100%"
            preserveAspectRatio="xMidYMid meet"
            xmlns="http://www.w3.org/2000/svg"
            style={{ display: "block" }}
            ref={svgRef}
        >
            <defs>
                <linearGradient
                    id="MYGradient"
                    x1="0" y1="332" x2="0" y2="0"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop offset="0%" stopColor="#014866ff" />
                    <stop offset="100%" stopColor="#4b8aa3ff" />
                </linearGradient>

                <linearGradient
                    id="HighlightGradient"
                    x1="0" y1="332" x2="0" y2="0"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop offset="0%" stopColor="#b6f1d9ff" />
                    <stop offset="100%" stopColor="#02034dff" />
                </linearGradient>

                {/* Animated Shadow Filter */}
                <filter id="mapDropShadow" x="-30%" y="-30%" width="160%" height="160%">
                    <feDropShadow
                        dx="10"
                        dy="10"
                        stdDeviation="5"
                        floodColor="#87d3ffff"
                        floodOpacity="0.25"
                    >
                        <animate
                            attributeName="stdDeviation"
                            values="5;8;5"
                            dur="4s"
                            repeatCount="indefinite"
                        />
                        <animate
                            attributeName="flood-opacity"
                            values="0.25;0.4;0.25"
                            dur="10s"
                            repeatCount="indefinite"
                        />
                    </feDropShadow>
                </filter>
            </defs>
            <g id="features" filter="url(#mapDropShadow)">
                {/* All your SVG paths here (keep them as in your original code) */}
                <path d="M891.1 164l-2.6 0-3.2 0-2.1 0-1.6-0.3-0.3-0.3-2.4-2.1-0.2-0.6 0.3-0.6 0.9-0.6 0.9-0.4 0.8-0.2 0.8 0 1.7 0.6 0.8 0.1 0.5 0.2 0.5 0.6 0.6 0.9 0.4 0.5 0.4 0.2 0.5 0.1 0.6 0.1 2.2 0.7 0.2 0.4 0.3 0.7z m-105.1 0.5l-0.2-0.4-0.2-0.6-0.1-0.7-0.1-1.1 0.1-0.7 0.2-0.5 1.4-1.8 0.2-0.4 0.3-1 0.1-0.5-0.1-0.5-0.2-0.6-0.7-0.8-0.4-0.4-0.4-0.3-0.4-0.1-0.7-0.2-0.4-0.1-0.3-0.2-0.3-0.3-0.2-0.6-0.1-0.8 0-1.1 0-0.3-0.2-0.5-0.7-1.1-0.2-0.7-0.2-3.5 0.1-0.4 0.1-0.3 1.5-2.6 0.1-0.5 0.1-0.5 0.2-1.6 0.1-0.9 0.2-0.4 0.2-0.3 1-1 0.2-0.4 0.2-0.5 0.7-2.3 0.1-0.5-0.1-0.5-1-4.4-0.3-0.5-0.3-0.3-0.6-0.1-0.9 0-1.3 0.2-2.2 0.1-4.3-0.4 1.2-1.1 1.5-1.9 0.5-0.3 1-0.1 0.9-0.4 0.7-0.6 0.4-0.5 0.3-0.7 0.3-1.9 0.1-1.7 0.1-0.5 0.5-0.9 0.4-0.4 0.4-0.3 0.2-0.3-0.3-0.6-1.8 0.8-1.7-0.1-1.6-0.6-4.1-3.6-0.1-0.4 0.1-0.4 0-0.3-0.5-0.2-0.2 0.2-0.9 0.7-0.3-0.5 0.1-0.7 0.3-0.7 0.3-1.5 0.4-0.6 0.3-0.5 0.1-0.4 0.6-0.5 2.4-1.4 0.5-0.9 0.6-0.7 3.2-2.4 1.6-3.5 1-0.9 0.6 1-1.6 3 1 1.4 0.2-0.5 0.2-1.3 0.2-0.5 1.6 1.4 1 0.4 4.1 0.3 1.3-0.1 1.2-0.4 1.4-1.2 1.2-1.8 1-2.1 0.5-1.9 0.4-2 0.2-0.9 0.5-0.8 0.7-0.5 4.2-2.3 0.7-0.8 0.3-1.1 0.1-2.2 0.3-0.9 2.9-4.6 0.6-2-0.7-1.4-1 0.4-0.4-0.6 0.5-0.9 1.4-0.4 0.8-0.5 2.2-3.3 1-0.9 0.6-0.3 0.7-0.1 0.6 0.2 0.7 0.3 0.6 0.1 0.2-0.5-0.2-1.5-0.6-0.2-0.8 0.5-1.2 0.1 0-0.3 1.2-0.6 0.8-0.8 0.2-0.2 0.3-0.1 0.9 0.1 0.5-0.3 0.1-0.1 0-0.3 0.4-1.5 0.1-0.3 0.2-0.4 0.5-0.5 4.1-3.1 0.7-0.7 0.8-0.6 0.9-0.4 0.7-0.6 0.2-0.9 0.1-0.9 0.1-0.9 0.4-0.7 0.6-0.3 0.8-0.6 2.9-3.9 0.7-0.6 0.4-0.7 0.3-0.8 0.1-1.2-0.1-1.6 0.1-0.4 0.2-0.4 0.6-0.8 0.2-0.5-0.1-1.7 0.1-0.8 1.4-0.6 0.3-0.8 0.5-1.8 0-1 0.1-0.4 0.4-0.2 0.2-0.2 0.5-0.7 0.2-0.6 0.4-0.4 0.5-0.3 0.6 0 0.1 0.1 0.5 1.1 1.9 1.3 0.3 0.5 0.6 3.3-0.9-0.5-0.5-0.1-0.2 0.2-0.3 1.4 0 0.3 0.5 0.5 0.8 0.2 0.6 0.4 0.3 1-0.2 1.8-0.5 1.3-1.5 2.2-0.8 1.6-0.7 2 0.3 1.7 1.8 0.4 1-0.5 0.7-0.8 0.6-0.9 0.4-0.3 0.7-0.3 2-2 1.5-1.1 0.3-0.5 0.2-1.7 0.3-0.2 0.3 0 0.3-0.3 0.6-1.3 1.7-1.7 0.2-0.7-0.2-0.9-1.1-2.1 0.1-1 0.3-0.5 0.4-0.1 0.4 0.2 0.6 0.1 0.4-0.2 0.3-0.3 0.1-0.3-0.1-0.2-0.4-0.2-0.1-0.5 0.1-0.5 0.2-0.3 0.5-0.1 0.5 0.1 0.6 0.2 0.3 0.1 0.4-0.3 0.7-0.6 0.3 0.1 0.4 0.2 1.2 0.5 0.3 0.1 0.2 0.2 2.4 1.4 0.2 0.2 0.2 0.4 0.1 0.8-0.3 2.2-0.2 0.5-0.7 0.4-0.2 0.8 0.1 0.9 0.3 0.8 1.7 2.2 0.2 0.8 0.1 2.9 0.2 0.8 0.6 1 0.6 0.3 1.8 0 0.7 0.2 0.7 0.5 1 1.2 1.1 1.1 0.8 0.6 0.8 0.2 0.7-0.2 0.6-0.5 1.1-1.8 0.7-0.9 0.4 0 0.1 0.7 0.1 1.3 0.1 0 1.1 1.1-0.2-0.1-0.1 0.2 0 0.3 0 0.3 0.2 0 0.6 0.2 0.2 0.1 0.6 0.6 0.5 0.2 0.6 0.1 0.6-0.3 0.5-0.1 0.5 0.3 1.1 1.2 0.5 0.8 0.5 1 0.3 0 0.1-0.4 0.3-0.2 0.2 0.1 0.3 0.2 0.2 0.3 0.1 0.3 0 0.5 0 0.5 0.2 0.4 0.4 0.8 0.1 0.4-0.2 0.4-0.3 0.4-0.2 0.4 0.5 1.3-0.5 3.1-0.3 0.7-0.7 0-1.5-0.2-0.9 0.2-0.8 0.6-1.3 1.2-0.4 0.8 0.2 0.7 1 1.2 0.7 1.8 0.7 0.3 0.2 0.3 0 0.5 0.2 0.3 0 0.4-0.2 0.6-0.4 0.4-0.4 0.1-0.4 0.2-0.1 0.6 0.3 0.7 0.6 0.2 0.7 0.3 0.3 1.1-0.3 0.8-0.6 0.6-1.6 1-5.9 2-1.1 1.2 0.8-0.1 2.6-1.2 1.2-0.3 3.3 0 3.1-0.4 2.3 0.3 0.8-0.1 0.3-0.5-0.4-1.2 0.5-0.3 0.5 0 0.5 0.2 0.7 0.1 0.8 0 2.2-0.5 0.5-0.3 0.2-0.6 0.7-0.6 1.2-0.8 1.5-2 0.9-0.4 0.2-0.3 0.3-0.1 0.6 0.2 1.6 1.6 0.1 0.5 0 0.5 0.1 0.4 0.8 0.4 0.1 0.6-0.1 0.9 0.2 1.1 2 2.6 0.2 0.3 0.1 0.3-1.4 1.8-0.7 0.4-1 0.3-1.1 0.1-0.9-0.4-0.7 0.3-2.9-0.3-0.8 0.3-0.5 0.6-0.1 0.5 0.5 0.3 0.2 0.3 1.3 3.7 0.4 0.3 0.2 0 0.3-0.1 0.6-0.1 0.7-0.2 1.8-0.9 0.8-0.2 0.4 0.1 0.3 0.4 0.4 0.2 0.4-0.1 0.4-0.2 0.5-0.1 0.5 0.2 0.4 0.7 0.4 0.2 0.4-0.2 0.4-0.6 0-0.4-0.2-0.5 0.3-0.8 0.2-0.3 0.3-0.3 0.1-0.4-0.4-0.9 0.1-0.5 0.7-0.5 2.1-0.2 1.9-0.9 1 0.2 2.4 1.3 0.2 0.1 0.3-0.2 0.5-0.6 0.4-0.1 0.4 0.2 0.5 0.2 0.3 0.2 0.9 0.2 4.7 2.8 1.6 1.3 0.7 0.4 0.8 0.4 0.7 0.4 0.2 0.6-0.1 0.5-0.4 0.2-0.3 0.2-0.2 0.2-0.4 2.6-0.3 1-0.2 1 0.3 1 0.4 0 0.4-1.2 0.3-0.5 0.4-0.4 0.2-0.5-0.1-1.2 0.2-0.5 1.2-0.9 1 0 0.9 0.7 2.2 2.6 0.9 0.9 0.8 0.3 1.2 0.2 0.9 0.7 0.6 0.9 0.3 1.1 0.4-0.3 0.1-0.2 0.1-0.3 0-0.5 2.2 1.7 0.8 0.2 1 0.5 0.8 1 0.2 0.9-0.9 0.5 0.5 0.4 0.8 0.3 0.7 0 0.6-0.4 0.1-0.6-0.3-0.5-0.1-0.4 0.6-0.4 1.2 0.1 2.1 1.1 1.4 0.1 3.7-1.7 1.1-0.2 0.8 0.3 0.6 0.7 1.1 1.5 1 1 0.5 0.6 0.1 0.8-0.1 1-0.4 1.6-0.1 0.8 0.2 2.1-0.1 1.2-1 0.9-2.3 2.5-1.8 1-8.5 3.1-8.6 2.5-1.7 1-1.1 0.5-1.1 0.3-1 0.1-0.7-0.1-1.5-0.7-1.3-0.2-0.9-0.3-0.6 0.1-0.6 0.1-1.4 1.1-1 0.6-0.4 0-0.6-0.4-0.3-0.3-0.8-1.5-1.6-1.8-1.5-0.8-0.9-0.3-0.4 0.3-0.5-0.2-1 0.7-1.4 1.2-0.4-0.3-0.2 0.2-0.1 0.5 0.1 0.6-1.8-0.5-1.7 1-2.6 3-0.8 0.5-0.3 0.4-0.2 0.5 0.1 0.6 0.2 0.2 0.3 0.1 1.2 0.7 0.3 0.3 0.3 0.4 0.5 1.4 1.1 1.7 1.3 1.5 1.4 1.3 1.6 1.2 0.6 0.5 0.4 0.3 0.4 0.2 0.5 0 0.9 0.1 0.4 0.1 0.2 0.3 0.4 0.9 0.2 0.3 0.3 0.2 0.3 0.3 0.8 0.3 0.1-0.8 0.7-0.2 0.8 0.3 0.6 0.4 0.5 0.6 0.1 0.6-0.2 0.6-0.5 0.6-0.2 0.5 0.4 0.6 0.6 0.4 0.3 0.4 0.3 0.3 0.9-0.5 1.3-1 0.5 0.3 1.2 0.2 0.5 0.2-0.2 0.6 0.5 0.9 1.8 2 0.1 0.3-0.4 0.5-0.5 0.3-0.4-0.1-0.5-0.2-0.7-0.1 0.2 0.7-0.2 0.7-0.5 0.6-0.4 0.5-0.7 0.5-0.7 0.2-1.8-0.1-0.8 0.1-1.5 0.4-0.9 0.2-2 0-1.1-0.2-0.7-0.5-0.6 0.8-0.3 0.2-4.2 1.2-3.8 0.4-1.1 0.3-6.1 3.1-1 0.1-2.9-0.9-0.8-0.1-0.5-0.2-1.8-1.7-0.7-0.4-0.4-0.2-0.4 0-0.1-0.2-0.3-0.9-0.2-0.2-0.6-0.1-1.5-0.5-1.1-0.3-0.5-0.1-1-0.7-0.5-0.8-0.4-0.9-0.8-0.9-0.7 0.1-1 0.6-0.9 0.8-0.4 0.5 0.2 0.4 0.9 0.6 0.2 0.5 0 1.9 0.2 0.5 0.7 0.7 0 0.5-0.2 0.6-0.4 0.4-0.9 0.7-0.4 0.3-0.2 0.4-0.3 0.3-0.1 0.4 0.2 0.2 0.8 0.8 0.3 0.4-0.5-0.1-0.4 0.1-1.9 0.8-0.4-0.4-0.7-0.3-1.3 0.1-0.8 0-1.5-0.3-1.3-0.3-1.1-0.7-2.6-2.8-3.8-1.9-1.7-1.2-0.4-0.5-0.2-0.4-0.4-0.3-0.7-0.1-0.5 0.1-0.8 0.7-0.4 0.2-8.5 0.2-2.6-0.7-2 0-3.6 0.7-1.8-0.4-1.7-1.2-0.8-0.1-1.5 1.2-0.8 0.3-0.9 0-1.2-0.5-1.2 0.6-0.6 0-2.1-3-0.5 0-0.3 0.5-1 1-0.3 0.7 0 0.5 0.2 0.3 0.1 0.3-0.3 0.4-0.9 0.2-0.5-0.2-0.5 0-1.1 1.1-0.5 0.2-0.5-0.1-0.4-0.2-1.2-2-0.2-0.6-0.2-0.2-0.3-0.1-0.3 0-0.3 0-1.3-1.2-1.1 0.2-1.3 0.7-1.4 0.3-0.6 0-2.1-0.6-1-0.1-0.5 0-0.5-0.3-0.4 1-0.4 0.6-0.4 0.4-0.9 0.5-2.2 2.2-0.6 0.3-0.8-0.3-0.4-0.5-0.3-0.8-0.2-0.7-0.3-0.4-0.6-0.3-1.1-0.4-1.9-0.1-1-0.3-0.4-0.6-0.2-0.8-0.4-0.4-0.6 0.1-0.6 0.6-0.4 1.5 0 1.5-0.2 1.4-1.8 1.5-0.1 0.6-0.3 0.5-0.6 0.2-0.5-0.3-0.3-0.4-0.3-0.1-2 1.8-0.3 0.4-0.3 0.3-1 0.3-0.4 0.4-0.4 1.1z m119.5-79.5l0.7 0.2 0.1 0.5-0.2 0.4-0.6 0.2-0.3 0.1-0.8 0.3-0.3 0-0.3 0-0.4-0.1-0.4-0.3 0-0.3 0.2-0.4 0.5-0.4 0.6-0.1 1.2-0.1z m23.5 63.4l1 0.3 0.6 0.7-0.2 0.7-1.1 0.2-1.7 0-0.7 0.7-0.3 0-0.2-0.1 0-0.2-0.1-1-0.2-0.2-0.6-0.5-0.2-0.2-0.1-0.5 0.2-0.1 0.4 0 0.2 0 0.6-0.1 2.4 0.3z m-10-7.2l0.6-0.8 1.2 0 1.3 0.5 1 0.5 0 0.4-0.7 0-0.4 0.3-0.2 0.6 0.1 0.7-0.9-0.9-0.3-0.1-0.2 0.2-0.2 0.5-0.2 0.3-0.3-0.2-0.7-0.5-0.8-0.4-0.7-0.2-0.6 0-0.2-0.1-0.4-0.4-0.2-0.2-1.6-0.2-3.2-1 0-0.3 3.7 0 0.5-0.2 0.3-0.3 0.3-0.3 0.3-0.2 0.9 0.2 0.4 0.6 0.4 0.8 0.8 0.7z m-48.2-98.6l1.1 1.5 0.8 1.4-0.2 1.4-1.7 1.5-1.2 0.3-1.4 0-1.5-0.2-1.1-0.4-0.5-0.3-0.1-0.4 0.1-0.4 0.4-0.3 0.5-0.2 2.3-0.1 0.8-0.2 0.5-0.4 0.2-0.8-0.4-0.4-1.7-0.1-0.1-0.6 0.5-0.6 1.9-0.2 0.8-0.5z m-21.6-27.5l0.6 0.6 0.3 1.1-0.1 1.2-0.5 0.6-0.8-0.8-0.7 0.4-0.3 0.8 0.5 0.9-4.4 1.1 0.2 0.4 0.4 0.3 0.3 0.3-0.3 0.5-0.4 0.2-0.6 0-0.7 0-0.5-0.2 2.2-4.2 0.7-0.3 1.6-0.4 0.6-0.3 1.9-2.2z m7.8 0.6l3.5-0.6 1.1 0.3 0.5 0.8 0.1 1.1-0.5 3.6-0.5 1.4-0.7 0.9-0.8-0.4-0.9 0.3-2.7 0.5-0.5 0.3-0.3 0.3-1.2 0.5-0.5 0.3-0.3 0.6-0.2 0.5-0.2 0.4-0.5 0.4-0.4-1.1-0.3-3.6 0.2-0.7 0.2-0.4-0.1-2 0.2-0.5 0.3-0.2 0.7-0.2 0.4-0.3 1.2-1.1 1-0.7 0.6-0.3 0.6-0.1z"
                    id="MY12"
                    name="Sabah"
                    fill="url(#MYGradient)"
                >
                </path>
                <path d="M79.4 53.8l-0.1 0.7 0 0.1 0 0.4-0.5 2.1-0.1 0.2-0.2 0.2-1.1 0.7-0.5 0.4-5.1 5.8 0 0.1-0.1 0.2-0.1 0.5-0.2 0.6-0.3 0.4-1.1-1.4-1-2-0.8-2.2-0.2-2-0.2-0.9 0.4-0.7 0.4-1 0.2-1.2-0.1-0.4-0.1-0.8 0-0.4 0.3-0.5 0.4-0.4 0.3-0.4-0.1-0.6-0.3-1.1-0.1-0.8 0.3-1.9 0.1-1 0.1-0.6 0.3-0.4 0.7-0.2 0.4 0.3 0.1 0.1 0.2 0.3 0.4 0.2 0.7 0 0-0.1 0.7-0.4 0.4 0 0.6 0 0.5 0.2 0.3 0.3 1.7 5.2 0.5 0.9 0.9 0.8 1.4 0.7z"
                    id="MY09"
                    name="Perlis"
                    fill="url(#MYGradient)"
                >
                </path>
                <path d="M71.1 66.2l0.3-0.4 0.2-0.6 0.1-0.5 0.1-0.2 0-0.1 5.1-5.8 0.5-0.4 1.1-0.7 0.2-0.2 0.1-0.2 0.5-2.1 0-0.4 0-0.1 0.1-0.7 0.4 0.2 1.1 0.3 0.7 0.1 1.2-0.2 0.7 0.2 1.5 1.1 0.8 0.4 2.9 0.8 1.6 0.7 0.8 0.2 0.8-0.1 3-2.2 0.8 0 0.1 0.3-0.1 1.1 0.2 0.4 0.4 0.2 0.4 0.1 0.5 0 0.5 0.1 1.1 0.6 0.4 0.9 0.1 2.2 0.1 0.5 0.2 0.6 0.6 0.9 0.1 0.4-0.1 0.4-0.2 0.4 0 0.1-0.1 0.4 0.1 1.3 0.1 0.4 0.4 0.7 0.2 0 0.2-0.3 0.6-0.2 0.6 0.1 1.9 0.4 0.9-0.2 1.2-1.3 0.9-0.2 1.5 1.2 0.8 0.3 2.1-0.5 0.6 0.2 0.3 0.6 0.1 1-0.1 1.1-0.4 0.4-0.6 0.3-0.6 0.8-0.1 1 0.8 0.7 0.8 0.7 0.4 0.9-0.3 1.8-0.1 2.7-0.3 1.5-0.1 0.9-0.3 0.8-0.9 0.4-0.9 0.2-0.8 0.4-1.9 3.9-0.3 0.5-0.1 0.6 0.1 0.5 0.2 0.4-0.3 0-0.2 0-0.1 0.1-0.1 0.1-0.1 0.1 0.2 0.8 0.1 0.1 0.1 0.2 0.2 0.3 0.1 0.1 0.4 0.2 0.1 0.1 0.1 0.1 0.1 0.2-0.1 0.2-0.6 2.7-0.1 0.2-0.1 0.1-0.6 0.4-0.2 0.3-0.4 0.4-0.4 0.4-0.1 0.1 0 0.2 0.1 0.4 0 0.2 0.2 0.1 0.1 0.1 0.2 0 0.1 0.1 0.1 0.1 0.1 0.1 0 0.2 0 0.3-0.1 0.8 0 0.4 0 0.1-0.1 0.6-0.3 0.7-0.3 0.5-0.6 0.6-0.3 0.1-0.3 0.1-0.2-0.2-0.1-0.1-0.2 0.1-0.2 0.2-0.3 0.5-0.2 0.3-0.3 0.6-0.2 1.8 0 0.4 0 0.4 0 0.4-0.3 1.3-0.1 0.3-0.2 0.3-0.1 0.3-0.6 0.6-0.3 0.2-0.5 0.2-1.2 0.3-1.6 0.2-0.3 0.3-0.5 0.8-0.1 0.5 0 0.2-0.2 0.2-0.2 0.2-1.1 0.8-0.2 0.2-0.1 0.2-0.4 0.7-0.2 0.8 0 0.1-0.1 0-0.1 0.3-3.3 2.9-0.2 0.4-0.2 0.7-0.1 0.1-0.1 0.1-0.7 0.6-0.7 0.8-0.1 0.1-0.1 0.1-0.2 0-0.3 0.1-0.4-0.1-0.3-0.2-0.5-0.3-0.5-0.2-0.2-0.1-0.2-0.3-0.4-1.3 1.4 0.2 0.6 0 0.1 0 0.2-0.1 0.1-0.1 0.1-0.2 0.1-0.2 0-0.2-0.8-5.4-0.8-9.6 0.1-2.8 0.1-0.4 0.2-0.5-0.8-0.4-0.8-0.1-6.7-0.4-0.2-0.7-0.3-4 0.4 0.2 0.6 0.1 0.5 0 0.4-0.3 0.1-0.4-0.4-0.3-0.4-0.2-0.2-0.2 0.5-5.5-1.3-9.4-0.3-0.8-0.7-0.9-0.6-1-0.8-2.1-1.7-3-0.5-1.9-0.7-0.7-1.6-1 0-0.1z m-19.1 0.9l0.6-1.5 1.1-0.4 0.6-0.6 0.2 0.8-0.4 0.4-0.3 0.5 0 0.6-0.1 0.3 0 0.7 0.3 0.8-0.2 0.7-0.4 0.7-0.5 0.2-0.8-0.6-0.2-0.8-0.4-0.1-0.4-0.9 0.9-0.8z m5.3-5.8l0.5 0.8 0.1 0.4 0.1 0.4-0.5 0.2-0.6 0.2-0.4 0.5-0.6 0.3-0.2 0.7-0.8-0.5-0.2-1.1-0.9 0-0.8 0.3-0.8 0.9-0.6 0.7-0.8 0-0.5 0.6-0.6 0.3-0.4 0.1-0.6-2.2 0.3-0.8-0.1-1.2-0.8-0.3-1.2-0.2-0.8 0-0.4-0.6-0.2-0.8 0-2 1.1 0.4 0.9-0.4 1.5 0.1 0.7 0.5 1.6 0.2 0.7-0.2 0.7-0.6 0.6-0.8 0.4-0.1 0.7 0-0.2-0.4-0.1-0.3 0.5 0 0.4 0.2 0.3 1 0.4 0.7 0.5 0.5 1 0.4-0.1 0.4 0.4 0.2 0.4 0.3-0.2 0.6-0.4 0.6z"
                    id="MY02"
                    name="Kedah"
                    fill="url(#MYGradient)"
                >
                </path>
                <path d="M179.6 85.1l-0.1 0.1-1.7 2.1-3.6 3.3-1.6 1.7-0.2 0.6 0.6 2.6 0.1 0.3 0.2 0.5 0 0.2-0.1 0.8 0 2.5 0 0.2-0.1 0.1-0.4 0.3 0 0.4 0.3 2.8 0 0.7-0.1 0.5-0.1 0-0.1 0.2 0.1 0.2 0.8 1 1 0.8 0.2 0.2 0.1 0.5-0.2 1.1-0.6 1.2 0 0.2-0.2 1-0.1 2.7 0.1 0.7 0.1 0.2-0.1 0.2 0 0.3-0.3 0.8 0 0.2 0.1 0.2 0.3 0.1 0.6 0.2 0.2 0 0.2 0.2 0.1 0.1 0.2 0 0.2 0 0.2 0.1 0.2 0.1 0.2 0.2 0.3 0.2 0.2 0 0.6 0.8 0.2 0.1 0.2 0.2 0.2 0 0.1 0.1 0.2 0.1 0.4 0.5 0.4 0.3 0.1 0.1-0.1 0.2-0.2 0.3-0.1 0.1 0 0.2-0.1 0.1 0 0.2-0.2 0-0.3 0.1-0.1 0.1-0.1 0.1 0 0.3 0.5 2.2 0 0.3-0.1 0.1-0.1 0.1-0.2 0.1-0.1 0-0.1 0.2 0 0.1 0 0.1 0 0.1 0 0.2 0.1 0.4 0.4 1.3 0.1 0.7-0.1 0.4 0 0.9 0 0.2 0.2 0.2 0.5 0.2 0.3 0 0.1-0.1 0.1-0.1 0.1-0.1 0.2 0 0.1 0 0.2 0 1.1 0.5 2.3 1.7 0.2 0 0.4 0.1 0.1 0.2 0.1 0.2 0.1 0.7 0.1 0.4 0.2 0.3 0.2 0.2 0.1 0.2 0.4 1 0 0.3-0.2 0.7-0.9 0-0.4-0.2-0.5-0.3-0.1-0.1-0.2 0-0.2 0.1-0.2 0.2-0.1 0.2 0 0.2 0 0.2 0 0.2-0.1 0.1-0.1 0.3-0.1 0.2 0 0.1 0 0.2 0 0.1 0.2 0.3 0.1 0.1 0.1 0.7 0 0.4 0 0.2-0.1 0.1-0.1 0.1-0.2 0.1-0.6-0.1-0.7-0.1-0.2-0.1-0.2-0.1 0-0.1-0.2-0.1-0.1-0.1-0.1 0-0.2 0-0.2 0.3-0.2 0.3-0.1 0.1-0.1 0.1-0.2 0.2-0.1 0.1-0.3 0.2-0.3 0.2-0.2 0.1-0.2 0-0.4-0.1-0.3-0.1-0.1-0.1-0.5-0.3-0.8-0.5-0.2-0.2-0.2-0.4-0.1-0.1-0.2 0-0.3-0.1-0.4 0.1-0.3-0.1-0.2 0-0.3-0.1-0.2-0.1-0.4 0-0.6 0.2-0.4 0.1-0.4 0.4 0 0.2 0 0.1 0.1 0.2 0 0.1 0 0.2-0.1 0.1-0.5 0.7-0.5 0.4 0 0.1-0.3 0.6-0.2 0.1-0.4 0-0.7-0.1-0.4-0.1-0.2-0.1-0.9-0.6-0.3 0-0.3 0.1-1 0.2-0.3 0.2-0.2 0.1-0.5 0.6-0.9-0.1-0.2-0.1-0.2-0.1-0.1-0.2-0.1-0.2-0.1-0.1-0.1-0.1-0.1-0.1-0.2-0.1-0.1 0-0.1-0.1-0.1-0.2 0-0.2 0-0.1 0-0.2-0.1-0.2-0.1-0.1-0.3-0.3-1.6-1.3-0.1-0.1-0.1-0.1 0-0.2 0-0.4-0.1-0.1-0.2-0.8-0.1-0.1-0.1-0.1-0.4 0.1-2.6 1.1-0.4 0-0.3-0.2-0.3-0.2-0.1-0.1-0.1 0-0.2 0-0.2 0-0.1-0.1-0.4-0.6-0.1-0.1-0.1-0.1-0.1 0.1-0.2 0.1-0.2 0.2-0.2 0.1-0.3 0-0.2 0-0.1-0.2-0.2-0.2-0.1 0-0.2 0.1-0.3 0.4-0.1 0.2-0.1 0.2 0.1 0.2 0 0.2 0 0.4 0 0.3-0.5 2.1-0.1 0.3-0.1 0.1-0.5 0.5-0.3 0.4-0.1 0.3-0.1 0.1 0 0.2 0 0.5-0.1 0.2-0.2 0-0.3-0.1-0.2-0.1-0.1-0.2 0-0.1-0.3-0.6 0-0.2 0-0.1 0.1-0.4 0-0.2 0-0.2 0-0.1-0.1-0.2-0.1-0.1-0.1-0.1-0.8-0.4-0.2-0.2-0.2-0.1-0.2-0.1-0.2 0-0.1-0.1-0.1-0.1 0-0.2 0-0.2-0.1-0.2 0-0.1-0.1-0.2-0.1-0.1-0.1 0-0.2-0.1-0.3 0-0.2-0.1-0.1 0-0.1-0.1-0.1-0.2 0-0.2 0-0.4 0-0.4 0-0.2-0.1-0.1-0.1-0.1-0.1-0.1-0.2 0-0.3-0.1-0.4 0.1-0.2 0.1-0.2 0.1-0.1 0.2-0.2 0.5 0 0.3 0 1.1 0 0.2-0.2 1.1 0.1 1-0.1 0.3-0.1 0.7-0.1 0.1-0.1 0.2-0.2 0.1-0.3 0.1-1 0.3-0.3 0.1-0.4 0.4-0.3 0.3-1.4 0.7-0.4 0.3-0.2 0.2-0.2 0.3-0.2 0.3-0.3 0.2-0.3 0.1-1 0.3-0.3 0-0.2-0.2-0.4-0.2-0.7 0.1-0.5-0.4-0.3-0.4-0.2-0.1-0.7-0.2-0.2-0.1-0.2-0.2-0.1-0.1-0.1-0.1-0.4-0.1-0.6 0-0.5 0.1-0.3 0-0.1 0.1-0.6 0.2-0.2 0.1 0 0.1-0.1 0.2 0 0.2 0 0.2-0.1 0.1-0.3 0-0.6-0.1-0.5-0.4-0.2-0.2-0.7-0.6-0.5-0.2-1.9-0.5-0.4-1.2-0.3-0.7-0.9-1.3 0.6-0.8 0.1-0.1 0.2-0.1 0.1 0 0.2-0.1 0.1 0 0.2 0 0.1 0 0.2 0 0.1 0 0.1-0.2 0.1-0.3 0-0.5-0.1-0.6-0.1-0.1 0-0.2 0-0.2 0.1-0.1 0.2-0.2 0.2 0 0.2-0.1 0.2-0.1 0.1-0.2 0.2-0.5 0.1-0.3 0.1-0.2 0.1-0.1 0.1-0.2 0-0.2-0.1-0.4-0.3-0.8 0-0.1 0-0.2 0.2-0.2 0.3-0.5 0.1-0.1 0.1 0 0.4-0.1 0.1 0 0.4 0 0.2-0.2 0.1-0.2 0.5-1.4 0-0.3 0-0.2-0.3-1.9-0.1-0.3-0.5-0.4-0.4-0.9 0.8-0.5 0.1-0.2 0.1-0.2 0.1-0.4 0-0.4 0-0.2 0.1-0.3 0.2-0.2 0.1-0.2 0.1-0.7 0-0.4 0-0.4 0.1-0.2 0.6-1 0.1-0.3 0-0.3 0-0.2 0-0.2 0-0.2 0.2-0.3 0-0.2 0.1-0.2 0-0.2-0.1-0.4 0-0.2 0.1-0.2 0.1-0.5 0-0.4 0.1-0.7 0.1-0.1 0.1-0.3 0.6-0.9 0.2-0.2 0-0.2 0-0.8 0-0.2 0.3-0.5 0.1-0.2 0-0.5 0.1-0.1 0.1-0.1 0.3 0 0.3 0 0.2 0 0.1-0.1 0.2-0.2 0.1-0.4 0.2-0.2 0.7-0.6 0.2-0.2 0.1-0.3 0-0.2-0.1-0.1 0-0.2 0.2-0.3 0.2-0.2 0.6-0.4 0.3-0.2 0.2-0.2 0-0.1 0.2-0.1 0.1 0 0.3 0.1 0.3 0.4 0.2 0.1 0.1 0 0.4 0.1 0.4 0 0.3 0.2 0.5 0.3 0.1 0 0.2 0 0.3-0.4 0.2-0.1 0.4-0.2 0.2 0 0.2 0 0.2 0 0.2 0.1 0.2 0 0.1 0 0.2-0.1 0.2-0.1 0.5-0.4 0.2-0.1 0.1-0.3 0.1-0.2-0.3-0.7 0.1-1.3 0.1-0.5 0.1-0.2-0.1-0.3-0.2-0.5 0-0.4 0.1-0.4 0.2-0.4 0-0.1 0.1-0.2 0-0.2-0.1-0.3-0.1-0.3-0.2-0.4-0.2-0.2-0.2 0-0.1 0-0.2 0-0.3-0.2-0.1 0-0.1 0-0.2 0-0.2 0.2-0.2 0-0.2 0.1-0.1-0.1-0.4-0.2-0.1-0.1-0.2 0-0.4 0-0.1 0-0.2-0.1 0-0.2 0-0.2 0.3-0.9 0.3-0.6 0.2-0.5 0-0.2 0-0.3-0.1-0.6-0.1-0.2-0.1-0.2-0.4-0.3-0.1-0.1-0.1-0.1-0.1-0.2 0-0.3 0-0.6 0-0.5 0-0.1 0-0.4 0-0.2 0.1-0.3 0.3-0.4 0.3-0.3 0.5-0.4 0.1-0.1 0.2-0.7 0.1-2.4-0.1-0.2 0.8-0.8 0.5-0.1 0.7 0.1 0.3 0.5 0.3 0.6 0.4 0.5 1 0.5 0.8-0.1 0.5-0.6 0.4-1 0.2-0.2 1.4-0.9 0.2-0.4 0.2-0.4 0.2-0.4 0.5-0.3 1.6-1.3 0.3-1.8 0-2 0.7-1.9 1.1-1.4 3.4-2.7 1.3-1.2 0.5-1.5-0.2-3.6 0.3-2.5 1.2 0.9 2.4 1.7 0.6 0.2 1.1-0.3 0.2-0.5-0.4-0.4-0.8 0.3 0.5-0.8 1 0.1 5.4 1.9 1 0.7 2 2 5.5 11 1 1.2 1 0.8z"
                    id="MY03"
                    name="Kelantan"
                    fill="url(#MYGradient)"
                >
                </path>
                <path d="M85.2 118.6l0.4 1.3 0.2 0.3 0.2 0.1 0.5 0.2 0.5 0.3 0.3 0.2 0.4 0.1 0.3-0.1 0.2 0 0.1-0.1 0.1-0.1 0.7-0.8 0.7-0.6 0.1-0.1 0.1-0.1 0.2-0.7 0.2-0.4 3.3-2.9 0.1-0.3 0.1 0 0-0.1 0.2-0.8 0.4-0.7 0.1-0.2 0.2-0.2 1.1-0.8 0.2-0.2 0.2-0.2 0-0.2 0.1-0.5 0.5-0.8 0.3-0.3 1.6-0.2 1.2-0.3 0.5-0.2 0.3-0.2 0.6-0.6 0.1-0.3 0.2-0.3 0.1-0.3 0.3-1.3 0-0.4 0-0.4 0-0.4 0.2-1.8 0.3-0.6 0.2-0.3 0.3-0.5 0.2-0.2 0.2-0.1 0.1 0.1 0.2 0.2 0.3-0.1 0.3-0.1 0.6-0.6 0.3-0.5 0.3-0.7 0.1-0.6 0-0.1 0-0.4 0.1-0.8 0-0.3 0-0.2-0.1-0.1-0.1-0.1-0.1-0.1-0.2 0-0.1-0.1-0.2-0.1 0-0.2-0.1-0.4 0-0.2 0.1-0.1 0.4-0.4 0.4-0.4 0.2-0.3 0.6-0.4 0.1-0.1 0.1-0.2 0.6-2.7 0.1-0.2-0.1-0.2-0.1-0.1-0.1-0.1-0.4-0.2-0.1-0.1-0.2-0.3-0.1-0.2-0.1-0.1-0.2-0.8 0.1-0.1 0.1-0.1 0.1-0.1 0.2 0 0.3 0 0.4 0.5 0.5 1.1 0.3 0.4 0.5 0.2 0.9-0.1 0.4 0.1 1.1 0.7 0.8 1 0.7 1.2 0.6 1.2 0.7 0.3 1.3-0.4 2.2-1.3 0.7-0.7 0.6-0.8 0.4-0.9 0.1-1.1 0.6-2.1 1.4-0.7 1.9-0.3 1.7-0.9 1.7-1.1 4.9-1.9 1.6-0.6 1.1-0.3 1 0.3 0.7 1 0.3 0.5 0.2 0.3 0.4 0.3 0.5 0.1 0.4 0.1 0.1 0.4-0.2 0.5 0 0.4 0.9 2.1 0.7 0.8 1 0.2 0.4-0.2 0.1 0 0.1 0.2-0.1 2.4-0.2 0.7-0.1 0.1-0.5 0.4-0.3 0.3-0.3 0.4-0.1 0.3 0 0.2 0 0.4 0 0.1 0 0.5 0 0.6 0 0.3 0.1 0.2 0.1 0.1 0.1 0.1 0.4 0.3 0.1 0.2 0.1 0.2 0.1 0.6 0 0.3 0 0.2-0.2 0.5-0.3 0.6-0.3 0.9 0 0.2 0 0.2 0.2 0.1 0.1 0 0.4 0 0.2 0 0.1 0.1 0.4 0.2 0.1 0.1 0.2-0.1 0.2 0 0.2-0.2 0.2 0 0.1 0 0.1 0 0.3 0.2 0.2 0 0.1 0 0.2 0 0.2 0.2 0.2 0.4 0.1 0.3 0.1 0.3 0 0.2-0.1 0.2 0 0.1-0.2 0.4-0.1 0.4 0 0.4 0.2 0.5 0.1 0.3-0.1 0.2-0.1 0.5-0.1 1.3 0.3 0.7-0.1 0.2-0.1 0.3-0.2 0.1-0.5 0.4-0.2 0.1-0.2 0.1-0.1 0-0.2 0-0.2-0.1-0.2 0-0.2 0-0.2 0-0.4 0.2-0.2 0.1-0.3 0.4-0.2 0-0.1 0-0.5-0.3-0.3-0.2-0.4 0-0.4-0.1-0.1 0-0.2-0.1-0.3-0.4-0.3-0.1-0.1 0-0.2 0.1 0 0.1-0.2 0.2-0.3 0.2-0.6 0.4-0.2 0.2-0.2 0.3 0 0.2 0.1 0.1 0 0.2-0.1 0.3-0.2 0.2-0.7 0.6-0.2 0.2-0.1 0.4-0.2 0.2-0.1 0.1-0.2 0-0.3 0-0.3 0-0.1 0.1-0.1 0.1 0 0.5-0.1 0.2-0.3 0.5 0 0.2 0 0.8 0 0.2-0.2 0.2-0.6 0.9-0.1 0.3-0.1 0.1-0.1 0.7 0 0.4-0.1 0.5-0.1 0.2 0 0.2 0.1 0.4 0 0.2-0.1 0.2 0 0.2-0.2 0.3 0 0.2 0 0.2 0 0.2 0 0.3-0.1 0.3-0.6 1-0.1 0.2 0 0.4 0 0.4-0.1 0.7-0.1 0.2-0.2 0.2-0.1 0.3 0 0.2 0 0.4-0.1 0.4-0.1 0.2-0.1 0.2-0.8 0.5 0.4 0.9 0.5 0.4 0.1 0.3 0.3 1.9 0 0.2 0 0.3-0.5 1.4-0.1 0.2-0.2 0.2-0.4 0-0.1 0-0.4 0.1-0.1 0-0.1 0.1-0.3 0.5-0.2 0.2 0 0.2 0 0.1 0.3 0.8 0.1 0.4 0 0.2-0.1 0.2-0.1 0.1-0.1 0.2-0.1 0.3-0.2 0.5-0.1 0.2-0.2 0.1-0.2 0.1-0.2 0-0.2 0.2-0.1 0.1 0 0.2 0 0.2 0.1 0.1 0.1 0.6 0 0.5-0.1 0.3-0.1 0.2-0.1 0-0.2 0-0.1 0-0.2 0-0.1 0-0.2 0.1-0.1 0-0.2 0.1-0.1 0.1-0.6 0.8 0.9 1.3 0.3 0.7 0.4 1.2-0.6 1.8-0.1 0.8 0 0.3 0.3 1 0 0.1 0.1 0.1 0.5 0.5 0.2 0.4 0 0.3-0.1 0.2-0.1 0-0.2 0.1-0.1 0.1-0.4 0.9-0.1 0.4-0.1 0.2-0.2 0.2-0.1 0.1-0.3 0.3-0.4 0.7 0.6 0.4 0.5 0.7 0.2 0.2 0.2 0.1 0.3-0.1 2.2 0.6 0.5 0.1 0.2 0.1 0.1 0.1 0.1 0.2 0 0.2 0 0.1-0.2 0.3 0 0.1-0.2 0.4-0.1 0.2-0.1 0.1-0.1 0.2 0.1 0.2 0.2 0.2 0.3 0.2 0.2 0.1 0.1 0.5 0.1 1.9 0.3 0.6 0.1 0.1 0.3 0.3 0 0.2 0 0.2-0.1 0.2 0 0.3-0.1 0.5 0 0.2 0.1 0.2 0.2 0.1 0.4 0.2 0.7 0.1 0.1 0.1 0.4 0.1 0.3 0.2 0 0.1 0 0.1-0.2 0.3 0 0.3 0 0.5 0.3 1.2 0.2 0.6 0.2 0.3 0.1 0 0.4-0.2 0.1-0.1 0.2 0.1 0.3 0.2 0.2 0.1 0.4 0.4 1.2 1.6-1 2.8-0.1 0.8 0 0.1 0 0.1 0.5 0.5 0.1 0.4 0.3 3.4 0.2 0.8 0.2 0.2 0.4 0.5 0.1 0.4 0.1 0.3 0 0.5-0.2 2 0 0.4 0.1 0.2 0.2 0.1 0.2 0 0.4 0.2 0.4 0.7-0.8 0.2-0.1 0.1-0.2 0.2-0.2 0.3-1.6 2.1-1 0.9-0.4 0.4-0.4 0.2-0.2 0-0.3 0.1-0.2-0.1-0.1-0.1-0.1 0 0-0.1-0.3-0.5-0.1-0.1-0.2-0.1-0.2-0.1-0.2-0.1-0.1-0.1-0.1-0.1-0.1-0.1-0.1-0.1-0.1-0.1-0.4-0.1-0.1-0.1-0.1-0.1-0.1-0.1-0.2-0.6-0.1-0.1-0.3-0.5-0.1-0.1-0.2 0-0.1-0.1-0.6-0.1-0.3-0.1-0.3-0.1-0.1-0.1-0.2-0.1-0.2-0.2-0.1 0-0.1-0.1-0.1-0.1-0.1-0.1-0.1-0.2-0.1-0.1 0-0.2-0.1-0.2-0.2-0.1-0.5-0.2-0.4-0.1-0.6 0.1-0.3 0.1-0.2 0-0.1 0.1-0.1 0.1-0.1 0.2 0 0.2-0.2 0.3 0 0.1-0.1 0.1 0.1 0.2 0.1 0.1 0.3 0.3 0 0.1 0.3 0.6 0.2 0.9 0.1 0.8-0.5 0.6-1 0.6-0.4 0.1-1.2 0.3-0.7 0-0.2-0.1-0.1 0-0.2-0.1 0-0.1-0.1-0.2-0.2-0.3 0-0.1 0-0.2 0-0.2 0-0.2 0-0.1 0-0.4-0.4-0.3-0.3 0.1-0.7 0.3-0.1 0-0.2-0.1-0.3-0.3-0.2-0.2-0.2-0.1-0.1 0.1-0.4 0.2-0.3 0.1-0.2 0-0.1-0.1-0.3-0.5-0.2-0.2-0.2-0.2-0.1 0-0.1 0.2-0.1 0.1-0.1 0-0.2 0-0.1-0.1 0-0.1 0-0.3-0.1-0.2-0.4-0.3-0.1 0-0.2 0 0 0.2 0 0.2-0.2 0.1-0.5 0.4-0.3-0.1-0.3-0.1-0.2 0-0.1 0-0.4 0.3-0.1 0 0-0.1 0.1-0.3 0-0.1 0-0.2 0-0.1 0.1-0.1 0.2 0 0.4 0.1 0.2 0 0.1-0.1 0-0.2-0.1-0.1-0.2-0.2-0.1-0.1 0-0.1-0.1-0.4 0-0.1-0.1 0-0.2 0-0.1 0.1 0 0.2-0.1 0.1-0.2 0-0.2 0.2-0.1 0.1-0.2 0.3-0.2 0.1-0.1-0.1 0-0.1 0-0.1 0-0.2-0.1-0.3 0.1-0.6-0.1-0.2-0.2 0-0.1 0-0.4 0.1-0.4 0-0.2-0.1-0.1-0.2-0.1-0.2-0.1-0.2-0.2 0-0.2 0-0.1 0-0.2 0.1-0.1 0.1-0.1 0-0.3 0-0.1 0-0.1 0.1-0.1 0.1-0.1 0.1 0 0.2-0.1 0.3 0 0.1-0.2 0.1-0.3 0.1-0.2 0-0.2-0.1-0.1-0.1 0-0.2 0-0.1 0-0.2 0.1-0.3 0.1-0.3 0.2-0.2 0.1-0.1-0.1-0.2-0.2-0.3-1.2-0.7 0-0.1-0.1-0.1 0-0.2 0.1-0.1 0-0.2 0.1-0.1 0-0.2-0.2-0.3-0.1-0.1-0.2 0-0.4 0.1-1.3 0.3-1.7 0.2-0.8 0.2-0.2 0.1 0.1-0.1 0.1-0.3-0.5-0.2-1 0.2-0.5 0.1-0.3 0.2-0.5 0.2-1.1-0.3-1.2-0.6-0.6-0.4-0.6-0.7-0.2-0.7-0.2-0.9 0.1-2.3 0.2-0.8 0.6-0.3 2.9 0.1 0.9-0.1 0.7-0.9 0.8-0.4 1-0.1 0.7 0.1-0.4-1-1.1 0.1-1.1 0.6-0.5 0.3-1.2-0.1-0.2-0.7 0.1-1.1 0-1.2-0.5-0.7-2.4-1.6-0.7-0.7-0.9-0.7-1.1-0.2-1 0.7-0.5-1-0.4-1.5-0.2-1.4 0.3-0.6-0.7-0.2-0.2-0.6-0.1-0.7-0.2-0.7-0.5-0.6-0.4-0.1-0.2-0.2 0.2-1 0.4-1.2 0.2-0.7 0-0.8 0.2-0.7 0.9-0.6 0.2-0.6 0.1-1.3 0.9-3.1 0.6-1.2-0.7 0.3-0.9-0.3-0.7-0.6-0.3-0.9 0.2-0.9 0.4-0.8 0.6-0.5 0.8 0.5 0.3-0.7 0.4-0.5 0.4-0.4 0.4-0.3-1.1-0.2-1 0.3-0.7 0.1-0.3-1.3-0.2-2.3 0.4-0.6 1.4-0.1 0-0.4-0.3 0-0.7-0.3 0.4-0.6-0.1-0.3-0.9-0.4-0.5 0-0.3 0.1-0.2 0-0.3-0.7 0-0.6 0.3-0.8 0-0.5-0.3-0.2-0.4 0-0.4-0.2-0.1-0.6-0.5 0.3-0.5 0.1-1.1 0-0.5-0.2 0.2-0.4 0.5-0.4 0.2 0 0-0.6-0.2-0.2-0.3-0.2-0.5-0.4-0.5-0.3-0.5 0.1-0.5 0.3-0.3 0.4-0.6-0.7-0.2-0.6-0.2-0.6-0.3-0.7-1.1-1.3-0.3-1-0.7-1.6-0.4-0.6-0.3-1.1 0-0.4 0.2 0 0.2 0.1 0.2-0.2 0.5-0.5 0.5-0.4 0.2-0.3 0 0.1 3.5-0.9 0.5 0 0.6 0.2z"
                    id="MY08"
                    name="Perak"
                    fill="url(#MYGradient)"
                >
                </path>
                <path d="M776 126.6l4.3 0.4 2.2-0.1 1.3-0.2 0.9 0 0.6 0.1 0.3 0.3 0.3 0.5 1 4.4 0.1 0.5-0.1 0.5-0.7 2.3-0.2 0.5-0.2 0.4-1 1-0.2 0.3-0.2 0.4-0.1 0.9-0.2 1.6-0.1 0.5-0.1 0.5-1.5 2.6-0.1 0.3-0.1 0.4 0.2 3.5 0.2 0.7 0.7 1.1 0.2 0.5 0 0.3 0 1.1 0.1 0.8 0.2 0.6 0.3 0.3 0.3 0.2 0.4 0.1 0.7 0.2 0.4 0.1 0.4 0.3 0.4 0.4 0.7 0.8 0.2 0.6 0.1 0.5-0.1 0.5-0.3 1-0.2 0.4-1.4 1.8-0.2 0.5-0.1 0.7 0.1 1.1 0.1 0.7 0.2 0.6 0.2 0.4-0.2 0.8-0.5 4.1-0.6 2-0.9 1.8-0.4 0.5-1.7 0.9-0.3 0.4 0 1 0.7 0.7 1 0.5 0.6 0.7 0 1.5-1.8 5.4-0.2 1.8 0 1.9 0.2 1.1 1.1 2.8 0.1 0.5-0.1 0.9 0 0.5 0.1 0.6 0.8 1.3 0.4 1 0 0.5-0.3 0.5-0.5 0.1-0.4-0.1-0.4-0.2-0.4-0.1-1 1.1-0.9 2.1-1.6 7 0 0.8 0.8 0.9 0.7 0.2 0.1 0.4-1.6 2.1-0.3 0.9-0.2 1.9-0.4 0.9-0.7 0.4-1.9 0.2-3 1.3-1.5 0.2-1.3-0.6-1.2-1.8-0.6-0.2-0.8 1.2-0.8 1.7-0.6 0.6-2.6 1.8-0.8 0.7-0.6 1-0.2 0.5-0.1 0.5-0.2 0.5-0.3 0.5-0.7 0.6-0.4 0.1-0.1 0.2 0.3 0.7 1.2 1.5 0.3 0.9-0.5 1.2-0.5 0.3-0.5 0.1-0.4 0.2-0.3 0.6 0 0.4 0.5 0.9 0.2 0.5-0.2 0.9-0.4 1.1-0.1 0.9 0.7 0.7 0.5 0 1-0.5 0.5-0.1 0.5 0 0.6 0.2 0.6 0.3 0.4 0.3 0.7 0.7 0.8 1.1 0.5 1.1 0.1 0.8-0.6 0.9-1 0.3-2.1 0.2-1 0.6-1.7 2-0.7 0.4-0.8 0.4-2 1.7-0.9 0.6-1.8-0.2-0.6 0.3-0.3 1 0.1 1 0.1 0.7-0.3 0.6-1.2 0.4-3.6 0.5-1.4 0.8-0.7 1.8-0.5 2.5 0 0.9 0.2 0.4 0.5 0.7 0.1 0.5 0 1.5 0 0.4 0.5 1.1 0.5 0 0.8-0.3 1-0.2 0.5 0.4 0.2 0.6 0 0.7-0.1 0.6-0.4 0.9-0.2 0.8 0 0.8 0.1 1 0 0.8-0.6 0.6-2.6 1-2.5 0.3-0.5 0.2-0.5 0.4-0.4 0.4-0.2 0.4-0.2 0.5 0.1 0.3 0.1 0.4 0.2 0.6 0 1.4-0.5 3.2-0.8 2.4-1.2 2-0.7 0.8-1.7 1.3-0.5 0.8 0 1.1 0.2 1 0 1-0.2 0.9-0.8 0.7-1.1 0.3-1-0.4-0.2-0.1-3.4-2.5-1.1-0.5-1.1-0.1-0.9 0.2-6.9 2.9-0.1 0-1 0.7-0.4 0.2-0.6-0.2-0.5-0.5-0.4-0.5-0.5-0.4-1.7-0.5-1.9 0-6.6 1.1-0.9 0.3-1.1 0.9-2.7 3.5-7.4 4.9-1.1 0.3-1.3-0.6-2.4-2.8-1-0.6-0.9 0-1 0.2-1.8 0.8-2.2-0.1-3.4-2.6-2.2-0.8-2.7-0.6-2.1-0.1-0.6-0.2-1.5-1.2-1.7-0.8-1.8-0.2-1.8 0.3-1.8 0.6-0.7 0-0.1-0.4 0.2-0.5 1.5-1 0.7-0.8 0.5-0.9 0.3-1-0.1-1-0.4-0.7-0.8-0.4-1.1-0.4-0.8-0.2-2.2 0.1-0.9-0.2-0.8-0.2-0.8-0.1-0.8 0.3-0.5 0.4-0.3 0.4-0.3 0.4-0.5 0.3-0.4-0.1-1.2-0.3-13.8-0.7-1.1 0.2-0.8 0.5-0.6 0.7-0.8 0.6-0.8 0.3-2.9 0.2-1.1 0.4-5.3 2.5-0.9 0.7-0.3 1.2 0.2 0.4 1.1 0 0.2 0.4 0 0.6-0.1 0.5-1.5 2.9-2.4 7-0.4 0.5-0.6 0.3-1.2 0.2-3.7-0.1-2.9 1-4.1 4.9-2.7 0.7-1.4-0.4-2.4-1-1.2-0.4-0.9 0-0.6 0-3.9 1.6-1.1 1.5-0.6 0.3-0.5-1.1-0.2-0.8-0.2-0.7-0.5-0.4-0.8 0-1.8 0.6-1 0.2-0.9-0.1-1.1-0.3-6.2-2.7-0.9-0.1-10.4 2.6-2.9 0.1-1.4 0.3-1.3 0.6-2.6 2.2-0.4 0.7-0.1 0.8-0.1 0.4-0.3 0.2-0.3 0-0.4-0.4-0.2-0.1-0.5 0.3-0.1 0.1-0.9 0.4-0.2 0+0-0.2-0.3 0-0.5 0.3-0.8 0.8-0.7 0.3-0.6-0.1-0.4-0.3-0.3-0.4-0.3-0.2-0.6 0-0.1 0.1-0.2 0.3-0.5 0.6-1.9 0.9-2.2-0.2-2.2-1.1-1.7-1.4-1.9-2.6-0.8-0.7-0.6-0.2-2.3-0.5-0.7 0.1-0.6 0.2-0.5-0.2-0.1-0.9-0.2-1.2-2.3-3.3-1-2-0.7-0.9-0.8-0.5-2.7-0.3-0.9-0.4-1.1-0.7-0.9-1.8-0.9-0.8-1.7-0.8-0.6-0.5-0.5-1-0.9-2.7-0.6-0.7-0.8-0.5-0.9 0-0.9 0.2-0.9-0.1-0.9-0.7-0.8-2-0.3-0.3-0.9 0.5-0.3-0.1-0.1-1.3-0.3-0.4-4.1-3.6-0.9-1.1-0.5-1-0.3-0.9 0-2.1 0.3-3.6-0.3-0.9-0.8-0.3-2-0.1-0.6-0.2-0.5-0.4-0.5-0.6-0.3-0.5-0.2-0.6 0-0.4 0-0.3-0.3-1.7 0-0.7 0.4-0.6 1.7-1.4 0.5-0.6 0.7-1 1.2-2.2 0.9-2.4 0.2 0.8 0.1 1 0.5 1.9 0.1 0.9-0.2 0.8-0.2 0.6-0.1 0.7 0.5 1.1 1.3 1.9 0.9 0.9 0.9 0.7 2.9 1.2 1.6 1 1.9 0.8 0.8 0.6 0.5 0.8 0.8 1.6 0.6 0.8 0.6 0.1 2.3 0 0.6-0.1 0.7-0.4 1-0.1 5.9 0 0.6 0.2 0.9 0.6 0.5 0.2 0.2-0.2 0.8-0.6 0.3-0.2 0.4-0.1 0.3-0.1 0.3 0.2 1.1 1.2 0.3 0.1 0.2-0.8 0.5-0.5 0.1-0.3-0.1-0.3-0.2-0.5 0-0.2-0.1-1.4 0.1-0.5 0.5-1 0.8-0.4 0.6 0.2 0.4 1.3 0.4 0.6 0.1 0.3 0.1 0.3-0.1 0.7 0 0.3 0.2 0.3 0.2 0.3 0.3 0.2 0.3 0.1 0.3 0.2 0.4 0.1 1 0 0.5-0.2 0.6-0.8 1.4-0.5 0.8-0.5 0.7-0.1 0.6 0.9-0.5 0.7-0.4 0.6-0.3 0.7-0.1 1-0.1 0.3-0.8 0.9-0.3 0.5 1.2-0.5 0.7-0.1 0.6 0.3 0.5 0.5 0.1 0.5 0.2 0.4 0.5 0.2 0.4-0.1 1-0.5 0.5 0 0.6 0.2 1.9 1 2.7 0.8 1.2 0.7 0.6 1.1-0.3 1.1-0.8 0.9-1.8 1.1 0 0.4 1.2-0.1 1-0.1 0.9-0.5 0.9-0.8 0.4-0.8 0-0.9-0.2-0.9-0.1-0.7 1.3-1 2.1 0.3 4 1.6 1.9 1.3 3.6 3.6 1.7 1.2 2.8 1.3 0.6 0.2 1.1-0.1 1.5-0.8 1-0.3 1.5 0.6 1.7 1.3 1.8 0.9 2-0.9-0.8-0.1-1.3-0.4-0.6-0.2-0.2-0.3-0.4-1.2-0.4-0.3-0.7-0.1-1.3-0.5-0.7-0.1-0.5 0.2-1.7 0.8-1 0-0.9-0.3-0.7-0.5-0.7-0.7-0.8-0.6-2.1-0.8-0.7-0.6-0.9-2.2-0.9-0.9-0.2-0.6 0-1.3 0-0.4 0.7-1.6 0.4-1.7 0.4-0.6 2.7-0.5 1-0.1 0.9 0.4 0.6 0.8 0.6 1.1 0.7 0.6 1.1-0.5 0.6-0.1 0.6 0.5 0.8 0.4 1-0.3-0.9-0.3-0.5-0.2-0.8-0.6-0.4-0.1-0.4 0-0.9-0.2-0.3 0-0.2-0.1 0-0.6-0.5-0.7-0.1-0.2-0.2-0.2-0.8-0.2-0.3-0.1-0.2-0.2-1.1-1.8-0.2-0.6-0.1-0.7 0.2-0.3 1-0.3 0.4-0.2-0.1-0.3-0.7-0.4-0.2-0.4 0.2-1 0.4-0.8 1-1.6 1.4-3 1.4-4.3 0.1-1.1 0.2-0.2 0.6 0.1 0.9 0.3 1.1-0.1-1.2-1-0.1-1.1 0.7-0.6 0.8 0 0.9 0.4 1.7 0.3 0.4-0.1 0.6-0.4 0.3-0.3 0.9-1.2 0.1-0.4-0.4-0.4-0.5 0.2-0.5 0.5-0.4 1.2-0.4 0.1-0.5-0.2-1.3-0.8-0.9-0.2-3.4 0.5-0.6-0.1-0.3-0.5 0-1.1 0.3-0.7 0.7-0.7 0.9-0.5 0.9 0.2-0.4-0.9-0.9-0.3-0.8-0.2-0.4-0.3 0.1-0.9 0.5-2.1 0.5-3.3 0.6-1.3 1.1-0.9 0.4 0.8 0.6 0.4 1.6 0.5 0.5 0.4 1.2 1.4 0.4 0.3 0.2 0.2 0.3 0.2 0.4 0.2 0.3-0.1 1.5-1 0.4-0.1 1.6-0.2 0.3 0 0.9 0.7 0.9 0.5-0.4-0.9-0.9-0.9-1.5-1.4-0.4-0.6-0.6-1.9-0.3-0.7 1.4 0.1 1.3 0.3 1.1 0.1 0.9-0.8 0.3 0.1 0 0.1 0.1-0.1 0.3-0.1-1-0.4-1.8-0.4-0.7-0.5-0.6-0.5-0.5-0.5-0.2-0.7 0-1.1 0.8-2.9 0.2-1.8 0.3-0.7 0.5-0.5 3.4-2.5 5-3.2 1.4-0.7 8.9-1.7 7.1-0.2 13.1-3.5 12.8-3.4 7.1-1.5 2.9-1.1 0.5 0 8.1-2.5 1.5-0.8 2.5-2.7 1-1.7-0.6-1.1 0.3-0.4 1.2-0.8 2.3-2 0.4-0.2 0.2-0.5 1.6-2.4 3.6-3.2 0.8-1.3 0.2-0.3 0.1-0.4 0.1-1 0.2-0.5 1.1-0.8 0.2-0.3 0.4-0.5 2.2-2.5 1.1-1.9 0.3-1 0.2-1.1 0.3-0.9 0.9-0.7 1.9-1.1 11.3-9.7 9.7-12.6 1-2.3-0.4-1.8 1-1.5 0.5-2 0-2.2-1.1-4.4 0.3-0.7 1.1-0.3 3 1.4 2.6 0.4 0.9 0.5 1.5 1.2 1.2 0.4 0.5 0.4 0.4 0.9 0.3 0.2 0.7 0 0.3 0.1 0.1 0.3 0.1 0.4 0.1 0.3 0.5 0.5 0.3 0.6 0.3 1.3 0 1.3 0.7 0.9 0.3 2.1-0.2 0.4-0.6 0.6-0.2 0.3 0.1 0.7 0.2 0.4 1.6 0.1 2 0.4 1.9-0.3 0.9 0 0.6 0.6 0.9 3 0.5 0.9 0.7 0.9 2.9 2.6 0.9 0.7 0.4 0.4 0.2 0.5 0.4 1.1 0.2 0.4 1.9 0.2 2.2-1.3 4.3-4 0.3-0.7 0.3-1.9 0.3-0.9 1.1-1.6 0.3-0.9-0.2-0.8-1.4 0.6 0-0.9 0.2-0.5 0.3-0.3 1.1-1 0.5-0.8 0.4-0.8 0.4-1.8 0.1-1.3-0.3-0.2-0.7 0.1-0.9-0.3-0.9-1.3-0.2-1.7-0.3-5.5-0.1-1-0.3-0.9-0.9-1.8-0.3-0.8 0-0.8 0.1 0 0.4-0.4 1.7-0.7 0.7-0.5 1.5-1.5 0.9-0.5 1-0.3 0.9 0.1 0.8 0.1 0.8 0 0.8-0.5 0.3-0.7 0-0.7 0.1-0.8 1.1-1 0.5-0.2 0.4-0.3 0.4-0.4 0.3-0.4 0.3 1 0 1.8 0.3 1.7-0.3 3.7 0.2 3.7 0.4 1.8 1.5 3.7 0.5 1.8 0.6 4.6 0.4 0.6 0.6 0.4 2.6 1 1 0.2 1.4-0.1 0.4 0.1 0.7 0.6 0.5 0.2 0.9 0 0.4 0.1 0.8 0.6 1.1 0.5 1 0 0.7-0.7-0.2-1-0.6-1-1.7-1.5-1-1.4-0.6-1.9-0.3-1.9 0-1.8 0.4-1.9 0.1-0.8-0.2-1.1-3-8.8-1.2-1.6-0.6-0.4-0.6-0.3-0.5-0.4 0-1.1 0.8 0 0.7-0.1 0.5-0.3 0.5-1.6 0.5-0.4 0.6 0.1 0.6 0.6 0.1 0.3 0.2 0.1 1.1 0 0.4 0 0.5 0.2 0.3 0.3 0.5 0.9 0.8 0.3 1 0 0.7-0.1 0.6-0.4 1.5-1.6 0.2-0.3 0.7-0.5z m-185.7 119.3l0 0.2 0.1 0.4-0.2 0.3-0.4-0.3-0.5-0.2-0.4 0.2-0.3 0.4-0.2 0.5-0.5-0.7-0.8-1.9-0.5-0.4-0.9-0.4-0.2-0.9 0.3-2.1 0.1-1.9-0.8-7.6 0.1-2.4 0.6-1.4 1.6 0.6 1 1.4 0.5 2 0 8.1 0.4 3.4 1 2.7z"
                    id="MY13"
                    name="Sarawak"
                    fill="url(#MYGradient)"
                >
                </path>
                <path d="M78.3 98.4l6.7 0.4 0.8 0.1 0.8 0.4-0.2 0.5-0.1 0.4-0.1 2.8 0.8 9.6 0.8 5.4 0 0.2-0.1 0.2-0.1 0.2-0.1 0.1-0.2 0.1-0.1 0-0.6 0-1.4-0.2-0.6-0.2-0.5 0-3.5 0.9 0-0.1 0.2-0.3 0.4-1.5 0.6-1.4 0.1-1-0.1-0.5-0.4-0.9-0.1-0.3 0.1-0.6 0.4-0.8 0.1-0.5-0.1-0.6-1.1-2.2-1-1.3-0.2-0.6-0.1-1.1 0.3-4-0.3-1.1-1-1.8-0.1-0.3z m-2.1 5.5l0.4 0.7 0.7 0.4 0.5 0.4-0.9 1.6-0.2 1.8-0.2 0.8-1.1 2.2-0.1 0.8-2.5-0.4-1.9 0-0.1 0.1 0 0.1-0.1 0-0.3-0.1-0.2-0.2 0-0.2 0.6-0.4 0.2-0.6-0.3-2.5 0.1-2.1-0.1-0.5-0.2-0.3-0.2-0.3-0.1-0.2 0.1-0.5 0.2-0.3 0-0.3-0.3-0.4 0.6-0.2 0.2 0.3 0.2 0.4 0.4 0.2 0.4-0.1 0.3-0.3 0.2-0.4 0.3-0.2 0.8-0.2 1 0.1 0.9 0.2 0.7 0.6z"
                    id="MY07"
                    name="Penang"
                    fill="url(#MYGradient)"
                >
                </path>
                <path d="M100.8 178.6l0.2-0.1 0.8-0.2 1.7-0.2 1.3-0.3 0.4-0.1 0.2 0 0.1 0.1 0.2 0.3 0 0.2-0.1 0.1 0 0.2-0.1 0.1 0 0.2 0.1 0.1 0 0.1 1.2 0.7 0.2 0.3 0.1 0.2-0.1 0.1-0.2 0.2-0.1 0.3-0.1 0.3 0 0.2 0 0.1 0 0.2 0.1 0.1 0.2 0.1 0.2 0 0.3-0.1 0.2-0.1 0-0.1 0.1-0.3 0-0.2 0.1-0.1 0.1-0.1 0.1-0.1 0.1 0 0.3 0 0.1 0 0.1-0.1 0.2-0.1 0.1 0 0.2 0 0.2 0 0.1 0.2 0.1 0.2 0.1 0.2 0.2 0.1 0.4 0 0.4-0.1 0.1 0 0.2 0 0.1 0.2-0.1 0.6 0.1 0.3 0 0.2 0 0.1 0 0.1 0.1 0.1 0.2-0.1 0.2-0.3 0.1-0.1 0.2-0.2 0.2 0 0.1-0.1 0-0.2 0.1-0.1 0.2 0 0.1 0 0 0.1 0.1 0.4 0 0.1 0.1 0.1 0.2 0.2 0.1 0.1 0 0.2-0.1 0.1-0.2 0-0.4-0.1-0.2 0-0.1 0.1 0 0.1 0 0.2 0 0.1-0.1 0.3 0 0.1 0.1 0 0.4-0.3 0.1 0 0.2 0 0.3 0.1 0.3 0.1 0.5-0.4 0.2-0.1 0-0.2 0-0.2 0.2 0 0.1 0 0.4 0.3 0.1 0.2 0 0.3 0 0.1 0.1 0.1 0.2 0 0.1 0 0.1-0.1 0.1-0.2 0.1 0 0.2 0.2 0.2 0.2 0.3 0.5 0.1 0.1 0.2 0 0.3-0.1 0.4-0.2 0.1-0.1 0.2 0.1 0.2 0.2 0.3 0.3 0.2 0.1 0.1 0 0.7-0.3 0.3-0.1 0.4 0.3 0 0.4 0 0.1 0 0.2 0 0.2 0 0.2 0 0.1 0.2 0.3 0.1 0.2 0 0.1 0.2 0.1 0.1 0 0.2 0.1 0.7 0 1.2-0.3 0.4-0.1 1-0.6 0.5-0.6-0.1-0.8-0.2-0.9-0.3-0.6 0-0.1-0.3-0.3-0.1-0.1-0.1-0.2 0.1-0.1 0-0.1 0.2-0.3 0-0.2 0.1-0.2 0.1-0.1 0.1-0.1 0.2 0 0.3-0.1 0.6-0.1 0.4 0.1 0.5 0.2 0.2 0.1 0.1 0.2 0 0.2 0.1 0.1 0.1 0.2 0.1 0.1 0.1 0.1 0.1 0.1 0.1 0 0.2 0.2 0.2 0.1 0.1 0.1 0.3 0.1 0.3 0.1 0.6 0.1 0.1 0.1 0.2 0 0.1 0.1 0.3 0.5 0.1 0.1 0.2 0.6 0.1 0.1 0.1 0.1 0.1 0.1 0.4 0.1 0.1 0.1 0.1 0.1 0.1 0.1 0.1 0.1 0.1 0.1 0.2 0.1 0.2 0.1 0.2 0.1 0.1 0.1 0.3 0.5 0 0.1 0.1 0 0.1 0.1 0.2 0.1 0.3-0.1 0.2 0 0.4-0.2 0.4-0.4 1-0.9 1.6-2.1 0.2-0.3 0.2-0.2 0.1-0.1 0.8-0.2 1.3 2 0.6 0.6 0.3 0.1 0.8 0.4 0.2 0.1 0.2 0 0.4 0 0.2 0 0.9 0.4 1.5 0.9 0.3 0.2 0.2 0.5 0.1 0.1 0.2 0.2 0.1 0.2 0 0.1 0 0.2 0.2 0.4 0.7 1 0.4 0.7 0.1 0.4-0.3 1.4-0.1 0.2-0.1 0.1-0.1 0.1-0.1 0.1-0.3 0.1-0.2 0.1-0.1 0.1-0.8 1.5-0.2 0.4 0 0.3 0 0.2 0 0.2 0.1 0.1 0.1 0.2 0.3 0.5 0.1 0.3 0 0.1 0.4 1.5 0 0.3-0.1 0.2-0.4 0.7-0.5 1.3 3 3.9 0.2 0 0.1 0.1 0.5 0.1 1.7 0.9 0.3 0 1.2 0.2 0.3 0 0.2 0.2 0.1 0.1 0.2 0.2 0.2 0.5 0.3 0.5 0.1 0.3 0 0.2 0 0.2-0.1 1.2 0 0.2 0.1 0.2 0.1 0.4 0.3 0.5 0.1 0.2 0.1 0.1 0.1 0.3 0 0.2 0 0.3-0.2 1.5 0 0.6 0.1 0.7 0 0.8 0.2 0.6 0 0.4 0 0.1-0.1 0.2-0.3 0.1-0.3 0.2-0.2 0.1-0.1 0.2-0.1 0.3-0.2 0.6-0.1 0.6 0 0.4 0 0.1-0.1 0.4-0.1 0.1-0.2 0.2-0.8 0.6-0.1 0.1-0.1 0.2-0.1 0.2-0.3 0.9-0.1 1.1-0.2 0.5-0.1 0.2-0.3 0.3-0.5 0.4-0.3 0.1-0.3 0.1-0.2 0-1.7-0.3-1.4-0.1-0.4 0-0.7 0.1-0.1 0.4 0.1 0.8 0.5 2.7 0.1 1.9-1.2 2.5-0.3 0.6-0.1 0.2-0.1 0.9-0.1 0.3-0.2 0.4-0.3 0.6-0.1 1.1-0.4 0-0.6-0.4-0.4-0.5-0.8-0.5-2.6-0.5-0.9-0.4-1.6-0.3-1-0.4-1.8-1.3-1.1-1.3-2.3-3.5-1.4-1.2-2.1-0.5-2-0.2-1.2-0.8 0.8-2-1-0.2 0.1-0.6 2.3-2.8 0.7-0.7 0.6-0.2 0.5-0.5 0-1.2-0.3-2.3-0.3-0.6-1.3-1-0.3-0.6-1-6-0.2-1-0.7-0.7-1.6-1.4-0.2-0.3-0.1-0.4-0.3-0.4-0.9-0.2-0.5-0.2-0.4-0.3-0.1-0.2-0.3-0.8-2.2-2.9-1.5-1.4-0.3-0.4-0.4-1.1-0.9-1.3-0.4-2.1-0.4-1-0.7-1-3.4-2.3-1.1-0.5-0.5-0.3-0.2-0.3-0.8-1.3-1.2-1.5-0.7-0.6-2.2-0.3-0.5-0.5 0-0.8 0.5-1.1 0.8-0.7z m40.6 29.5l-0.4 0-1.1-0.5-0.5-0.6-0.3-0.1-0.1 0-0.1 0.1-0.1 0.1-0.3 0.2-0.6 0.3 0 0.1-0.1 0.1-0.2 0.3-0.6 1.1 0 0.2 0.1 1.6 0.1 0.2 0.2 0.3 0.1 0.1 0.1 0.2 0.1 0.1 0.6 0.3 0.2 0.2 0.1 0.2 0 0.2 0 0.2 0.1 0.1 0 0.1 0.1 0.1 0.1 0.1 0.1 0.3 0 0.2 0 0.1 0 0.1-0.3 0.4-0.1 0.1 0 0.2 0 0.2 0.1 0.3 0.1 0.3 0 0.2 0.2 0.1 0.2 0.1 0.3 0 0.3 0 1.3-0.1 0.3 0 0.1 0.1 0.1 0 0.1 0.2 0.1 0.2 0.1 0 0.1-0.1 0.1-0.2 0.1-0.3 0.1-0.1 0.2-0.1 0.1 0 0.2 0 0.1-0.2 0.1-0.1 0-0.3-0.2-1.8-0.2-0.9-0.1-0.5 0.1-0.3 0.1-0.1 0.2-0.2 0-0.2 0.1-0.7 0.1-0.2 0.3-0.2 0.1-0.2 0-0.2 0-0.3-0.1-0.2-0.2-0.4-0.2-0.5-0.2-0.2-0.1-0.2-0.1-0.1-0.2 0.1-0.5 0.2-0.2 0.1-0.3 0.1z m-1.7 15.4l0.4-0.1 0.6-0.6 0.2-0.3 0.2-0.4 0.9-1.1 0-0.8-0.3-0.5-0.4-0.3-0.1-0.9-0.4 0.3-0.2 0-0.2 0.2-0.7 0.1-0.3 0.1-0.1 0.3 0.1 0.3 0.2 0.2 0 0.3-0.2 0.1 0 0.3 0.2 0.7 0 0.3-0.1 0.1-0.1 0.2-0.2 0.2-0.1 0.4 0 0.3 0.2 0.4 0.2 0.1 0.2 0.1z m-19-8l-0.1 0.5-0.4 0.3-0.7 0.2-0.3-0.5 0.3-0.6 0.9-0.3 0.3 0.4z m3.1-0.2l0 0.3 0 0.6-0.1 1.1-0.6 0.6-0.9 0.3-0.7 0.5-0.3 0.3-0.2 0.2-0.4 0.1-0.1-0.3 0.1-0.5 1.1-2.8 0.7-1 0.8-0.3 0.6 0.3 0 0.6z"
                    id="MY10"
                    name="Selangor"
                    fill="url(#MYGradient)"
                >
                </path>
                <path d="M141.6 236.4l0.1-1.1 0.3-0.6 0.2-0.4 0.1-0.3 0.1-0.9 0.1-0.2 0.3-0.6 1.2-2.5-0.1-1.9-0.5-2.7-0.1-0.8 0.1-0.4 0.7-0.1 0.4 0 1.4 0.1 1.7 0.3 0.2 0 0.3-0.1 0.3-0.1 0.5-0.4 0.3-0.3 0.1-0.2 0.2-0.5 0.1-1.1 0.3-0.9 0.1-0.2 0.1-0.2 0.1-0.1 0.8-0.6 0.2-0.2 0.1-0.1 0.1-0.4 0-0.1 0-0.4 0.1-0.6 0.2-0.6 0.1-0.3 0.1-0.2 0.2-0.1 0.3-0.2 0.3-0.1 0.1-0.2 0-0.1 0-0.4-0.2-0.6 0-0.8-0.1-0.7 0-0.6 0.2-1.5 0-0.3 0-0.2-0.1-0.3-0.1-0.1-0.1-0.2-0.3-0.5-0.1-0.4-0.1-0.2 0-0.2 0.1-1.2 0-0.2 0-0.2-0.1-0.3-0.3-0.5-0.2-0.5-0.2-0.2-0.1-0.1-0.2-0.2-0.3 0 0.2-0.2 0.2-0.1 0.1-0.1 0.1-0.1 0-0.1 0.1-0.2 0-0.2 0.1-0.1 0.3 0.1 0.4 0.2 0.9 0.6 0.3 0.3 0.2 0.3 0 0.2 0 0.1 0.1 0.1 0.1 0.1 0.3 0.2 0.2 0.1 0.1 0.1 0.2 0.2 0.2 0 0.2 0.1 0.9 0 0.6 0 0.2 0 0.2 0.1 0.2 0.3 0.1 0.2 0.2 0.5 0.1 0.1 0 0.2 0 0.2 0 0.5 0.1 0.2 0 0.1 0.2 0.1 0.2 0 0.4-0.1 0.5-0.2 0.3-0.2 0.2-0.1 0.1-0.1 0.5-0.1 0.2 0 0.1-0.1 0-0.2 0.3 0.1 0.4 0.1 1.9 0.9 5.8 1.9 0.3 0 3.6 0.6 0.3 0.2 1.8 2 0.1 0.1 1.4 1.8 9.3 6.4 0.2 0.1 0.1 0 0.2 0 0.1-0.1 0.1-0.1 0.1-0.1 0.1 0 0.3 0.5 0.5 2.1-0.2 1.4-2.6 6.9-0.4 1.2-0.1 0.3 0.1 0.2 0.2 0.4 0.1 0.3 0 0.2 0 0.1-0.3 0.4-0.2 0.2-0.1 0.2-0.3 0.7-0.1 0.4-0.1 0.5 0 0.3 0.1 0.2 0.1 0.2 0 0.4 0 0.2-0.3 0.6 0 0.2 0.1 0.6 0.1 0.4 0 0.1 0 0.3-0.1 0.2-0.6 0.7-0.1 0.4-0.2 1.2-6.6-1.6-0.7-0.3-2.6-1.2-1.5 0-0.4 0.1-3.2 1-0.3 0.1-1.9-0.4-1.2-0.5-0.8-0.5-0.2 0-0.1 0-0.1 0.1-0.8 0.8-0.2 0.1-0.2 0.2-1.9 0.5-0.3 0-0.9-0.3-0.2 0.1-0.2 0 0 0.1 0 0.1 0 0.5-0.1 0.1-0.1 0.1-0.1 0.1-0.1 0.1-0.2 0.4-0.1 0.1-0.2 0-0.1-0.1-0.1-0.1-0.2-0.4-0.7 1-0.4 0.3-1.5 0.9 0-0.2-0.8-0.1-0.7-0.3-1.4-0.9-0.8 0.6-1.5 0.1-0.5 0.6-0.4-1.1-0.1-1.5-0.2-1.3-0.8-0.5-0.3-0.2-0.4-0.3-0.7-0.8-1-2.7-0.4-0.5-0.6-0.2-1.8-0.2z"
                    id="MY05"
                    name="Negeri Sembilan"
                    fill="url(#MYGradient)"
                >
                </path>
                <path d="M154 245.9l1.5-0.9 0.4-0.3 0.7-1 0.2 0.4 0.1 0.1 0.1 0.1 0.2 0 0.1-0.1 0.2-0.4 0.1-0.1 0.1-0.1 0.1-0.1 0.1-0.1 0-0.5 0-0.1 0-0.1 0.2 0 0.2-0.1 0.9 0.3 0.3 0 1.9-0.5 0.2-0.2 0.2-0.1 0.8-0.8 0.1-0.1 0.1 0 0.2 0 0.8 0.5 1.2 0.5 1.9 0.4 0.3-0.1 3.2-1 0.4-0.1 1.5 0 2.6 1.2 0.7 0.3 6.6 1.6-0.9 0.7-1.1 0.4-0.4 0.3-0.9 1.2-0.2 0.4-0.1 0.3 0 0.2 0.1 0.5 0 0.2-0.1 0.3-0.8 2.4-0.1 0.5-0.1 0.2 0.1 0.2 0 0.4 0.1 0.2-0.1 0.3 0 0.2-0.1 0.1-0.8 0.7-0.1 0.2 0.1 0.2 0.1 0.2 0.1 0.3 0.1 0.1 0.1 0.1 0.3 0.1 0.8 0.3 0.2 0.2 0.2 0 0.3 0 0.2 0.1 0.1 0.1 0.1 0.3 0 0.4-0.1 0.9-0.1 0.4-0.1 0.2-0.2 0-0.5-0.1-0.5 0.3-0.8 1.2-6.5-2.6-7.4-3.1-1.6-0.9-1.2-1-3.5-3.9-0.5-0.2-1-0.2-1-0.5-0.5-0.8 0.1-0.6z"
                    id="MY04"
                    name="Melaka"
                    fill="url(#MYGradient)"
                >
                </path>
                <path d="M182.2 244.5l0.2-1.2 0.1-0.4 0.6-0.7 0.1-0.2 0-0.3 0-0.1-0.1-0.4-0.1-0.6 0-0.2 0.3-0.6 0-0.2 0-0.4-0.1-0.2-0.1-0.2 0-0.3 0.1-0.5 0.1-0.4 0.3-0.7 0.1-0.2 0.2-0.2 0.3-0.4 0-0.1 0-0.2-0.1-0.3-0.2-0.4-0.1-0.2 0.1-0.3 0.4-1.2 2.6-6.9 0.2-1.4 2 0.7 2.5 1 0.6 0.4 6.6 7.9 1.6 1.3 7.3 3 0.3 0.1 0.1 0 7-2.6 1.9-0.4 0.7 0 0.5 0.3 0.6 0.4 2.6 1 0.3 0.2 0.4 0.4 0.2 0.4 0.1 0.1 6.6 3.9 0.4 0.2 0.2-0.1 0-0.2-0.1-0.2 0-0.1 0.1-0.1 0.1-0.1 0.5-0.7 0-0.1 0-0.2 0.1-0.3-0.1-0.2 0-0.3-0.1-0.2-0.2-0.3-0.2-0.3-0.1-0.1-0.2 0-0.4-0.1-0.1-0.1-0.1-0.1-0.1-0.1 0-0.1 0-0.1 0-0.1 0.1-0.3 0.1-0.1 0.3-0.4 0.1-0.1 0-0.1-0.3-0.6 0-0.2 0.1-0.1 0.1-0.2 0.1-0.1 0.1-0.1 0.1-0.2 0.1-0.1 0.1-0.3 0-0.2 0-0.1-0.1-0.2-0.1-0.1-0.1-0.2-0.1-0.1-0.2-0.1-0.3-0.2-0.1-0.1-0.1-0.1 0-0.2 0.2-0.2 1-0.5 0.1-0.1 0.4-0.7 0.1-0.3 0.6 0.3 0.7 0.1 0.8 0 0.3 0.2 0.7 0.6 0.5 0.2 0.4-0.1 1.1-0.5 0.7 0.3 0.1 0.3-0.1 0.4 0.1 0.4 1.6 1.3 0.6 0.7 1.2 2.6-0.4 1.1-0.1 0.9 0.4 0.8 3.4 4.3 0.4 0.4 1 0.8 0.3 0.3 0.2 0.5 0.1 0.9 0.1 0.5 0.3 0.5 0.8 1 0.1 0.6-0.1 0.4-0.4 0.9-0.1 0.6 0.3 0.9 2.2 2.7 1.2 2.9 0.8 1.2 0.6 1.1 0.3 0.5 0.9 1.1 0.3 0.5 0.4 1.1 0.1 0.9-0.2 2 0.3 0.9 0.5 1 0.6 0.6 0.6-0.5 0.3 0 1.2 2.2 0.5 2.2 0.8 1.2 0.3 0.7 0 1.5 0.3 0.7 0.5 0.6 0.4 0.6-0.2 0.5 0.3 0.6 0.3 1.9 1 2.6 0.4 2.1 0 1.2-0.3 0.5-0.4 0.4 0.2 2-0.3 0.7-1.7 0-0.2 0.2-0.5 0.7-0.2 0.1-2.1-0.1-0.9-0.2-1.9-0.7-0.4-0.3 0-0.5 0-0.9-0.2-0.7-0.6-0.4-0.6-0.5-0.5-0.4-0.2-1.7 1.2-0.8 1.7-0.7 1.1-1.3-0.8-0.2-0.9 0.2-0.7 0.5-1.6 1.1-0.3 0.2-0.2-0.3-0.3-0.9-1.4-3-1.1-1.6-1.1-0.8-0.2 0.6 0.4 1.3 1.8 2.7 0 0.7-0.2 1.3 0.1 0.6 0.2 0.6 0.1 0.7-0.1 0.8-1.7 1.3-2.2-0.5-4-2.1-1.2 0.1-1.9 0.9-0.9 0.3-1.9-0.2-0.7 0.1-0.8 0.4-2.3 3-1.4 1.1-1.7-0.3 0-0.2-0.4-0.5-0.3-0.4-0.2 0.3 0 0.5 0.3 0.7 0 0.4-0.1 0.5-0.8 1.2-0.7 1.6-0.6-0.9-1.6-1.4-0.6-0.9-0.4-0.9-1.5-5-0.9-1.8-1.3-1.5-2-1.2-5.5-2.1-7.8-4.9-1.9-0.9-1-0.3-1.1-0.1-0.7-0.2-0.6-0.5-0.7-1.2-0.9-1-1.1-1-1.3-0.8-1.5-0.7-0.7 0.3-2.3 0.3-0.8 0-1-0.6-0.6-0.9-0.9-1.9-0.8-0.9-3.8-3.1-0.5-0.5-0.3-0.6 0.4-0.1 0-0.2-0.1-0.2 0-0.2-0.6-0.5-0.8-0.7-1.8-1 0.8-1.2 0.5-0.3 0.5 0.1 0.2 0 0.1-0.2 0.1-0.4 0.1-0.9 0-0.4-0.1-0.3-0.1-0.1-0.2-0.1-0.3 0-0.2 0-0.2-0.2-0.8-0.3-0.3-0.1-0.1-0.1-0.1-0.1-0.1-0.3-0.1-0.2-0.1-0.2 0.1-0.2 0.8-0.7 0.1-0.1 0-0.2 0.1-0.3-0.1-0.2 0-0.4-0.1-0.2 0.1-0.2 0.1-0.5 0.8-2.4 0.1-0.3 0-0.2-0.1-0.5 0-0.2 0.1-0.3 0.2-0.4 0.9-1.2 0.4-0.3 1.1-0.4 0.9-0.7z m75.2-16.9l0.1 2.3-0.1 0.6-0.5 0.4-0.9 0.3-0.9 0-0.7-0.2-0.9-1-0.4-0.5 0-0.4 0.1-0.3-0.3-0.8 0.1-0.4 0.2-0.2 0.6-0.3 0.3-0.3 0.4-0.7 0-0.7-0.2-0.7-0.1-0.7 0.1-0.5 0.3-0.5 0.4-0.3 0.5 0.1 0.2 0.2 0.1 0.3 0.1 0.5 0.1 0.4 0.6 0.9 0.3 0.6 0.5 1.9z"
                    id="MY01"
                    name="Johor"
                    fill="url(#MYGradient)"
                >
                </path>
                <path d="M125.7 142.8l1.9 0.5 0.5 0.2 0.7 0.6 0.2 0.2 0.5 0.4 0.6 0.1 0.3 0 0.1-0.1 0-0.2 0-0.2 0.1-0.2 0-0.1 0.2-0.1 0.6-0.2 0.1-0.1 0.3 0 0.5-0.1 0.6 0 0.4 0.1 0.1 0.1 0.1 0.1 0.2 0.2 0.2 0.1 0.7 0.2 0.2 0.1 0.3 0.4 0.5 0.4 0.7-0.1 0.4 0.2 0.2 0.2 0.3 0 1-0.3 0.3-0.1 0.3-0.2 0.2-0.3 0.2-0.3 0.2-0.2 0.4-0.3 1.4-0.7 0.3-0.3 0.4-0.4 0.3-0.1 1-0.3 0.3-0.1 0.2-0.1 0.1-0.2 0.1-0.1 0.1-0.7 0.1-0.3-0.1-1 0.2-1.1 0-0.2 0-1.1 0-0.3 0.2-0.5 0.1-0.2 0.2-0.1 0.2-0.1 0.4-0.1 0.3 0.1 0.2 0 0.1 0.1 0.1 0.1 0.1 0.1 0 0.2 0 0.4 0 0.4 0 0.2 0.1 0.2 0.1 0.1 0.1 0 0.2 0.1 0.3 0 0.2 0.1 0.1 0 0.1 0.1 0.1 0.2 0 0.1 0.1 0.2 0 0.2 0 0.2 0.1 0.1 0.1 0.1 0.2 0 0.2 0.1 0.2 0.1 0.2 0.2 0.8 0.4 0.1 0.1 0.1 0.1 0.1 0.2 0 0.1 0 0.2 0 0.2-0.1 0.4 0 0.1 0 0.2 0.3 0.6 0 0.1 0.1 0.2 0.2 0.1 0.3 0.1 0.2 0 0.1-0.2 0-0.5 0-0.2 0.1-0.1 0.1-0.3 0.3-0.4 0.5-0.5 0.1-0.1 0.1-0.3 0.5-2.1 0-0.3 0-0.4 0-0.2-0.1-0.2 0.1-0.2 0.1-0.2 0.3-0.4 0.2-0.1 0.1 0 0.2 0.2 0.1 0.2 0.2 0 0.3 0 0.2-0.1 0.2-0.2 0.2-0.1 0.1-0.1 0.1 0.1 0.1 0.1 0.4 0.6 0.1 0.1 0.2 0 0.2 0 0.1 0 0.1 0.1 0.3 0.2 0.3 0.2 0.4 0 2.6-1.1 0.4-0.1 0.1 0.1 0.1 0.1 0.2 0.8 0.1 0.1 0 0.4 0 0.2 0.1 0.1 0.1 0.1 1.6 1.3 0.3 0.3 0.1 0.1 0.1 0.2 0 0.2 0 0.1 0 0.2 0.1 0.2 0.1 0.1 0.1 0 0.2 0.1 0.1 0.1 0.1 0.1 0.1 0.1 0.1 0.2 0.1 0.2 0.2 0.1 0.2 0.1 0.9 0.1 0.5-0.6 0.2-0.1 0.3-0.2 1-0.2 0.3-0.1 0.3 0 0.9 0.6 0.2 0.1 0.4 0.1 0.7 0.1 0.4 0 0.2-0.1 0.3-0.6 0-0.1 0.5-0.4 0.5-0.7 0.1-0.1 0-0.2 0-0.1-0.1-0.2 0-0.1 0-0.2 0.4-0.4 0.4-0.1 0.6-0.2 0.4 0 0.2 0.1 0.3 0.1 0.2 0 0.3 0.1 0.4-0.1 0.3 0.1 0.2 0 0.1 0.1 0.2 0.4 0.2 0.2 0.8 0.5 0.5 0.3 0.1 0.1 0.3 0.1 0.4 0.1 0.2 0 0.2-0.1 0.3-0.2 0.3-0.2 0.1-0.1 0.2-0.2 0.1-0.1 0.1-0.1 0.2-0.3 0.2-0.3 0.2 0 0.1 0 0.1 0.1 0.2 0.1 0 0.1 0.2 0.1 0.2 0.1 0.7 0.1 0.6 0.1 0.2-0.1 0.1-0.1 0.1-0.1 0-0.2 0-0.4-0.1-0.7-0.1-0.1-0.2-0.3 0-0.1 0-0.2 0-0.1 0.1-0.2 0.1-0.3 0.1-0.1 0-0.2 0-0.2 0-0.2 0.1-0.2 0.2-0.2 0.2-0.1 0.2 0 0.1 0.1 0.5 0.3 0.4 0.2 0.9 0 1 1.9 0.4 0.5 0.7 0.4 0.8 0.6 0.5 0.3 0.9 0.2 0.3 0.1 0.2 0.1 0.9 0.1 0.8 0 1.3-0.1 0.7 0.2 0.8 0.8 0.5 0.5 0 0.1 0.2 0.4 0 0.3 0.2 0.5 0 0.3-0.1 0.2-0.1 0.1-0.1 0.2-0.1 1 0 0.2-0.1 0.1-0.1 0.2-0.1 0.1-0.1 0-0.5 0.2-0.1 0.1-0.2 0.1-0.1 0.2-0.1 0.4 0.1 0.4 0.1 0.4 0.2 0.8 0.1 0.4 0.2 0.3 0.4 0.2 0.3 0.3 0.3 0.5 0.2 0.2 0.2 0.1 0.6 0 0.1 0 0.2 0.1 0.3 0 0.2-0.1 0.6 0.1 0.1 0 0.2 0.1 0.1 0.1 0.3 0.2 0.1 0.1 0.2 0 0.1-0.1 0-0.1 0.1-0.4 0.1-0.1 0.1-0.1 0.1-0.1 0.1-0.1 0.2 0 0.2 0.1 1.6 1.5 0.1 0.2 0.1 0.2 0 0.5 0.1 0.3 0.1 0.2 0.3 0 0.1 0.6-0.3 0.7-0.3 0.6-0.1 0.4-0.1 0.3 0.3 0.7-0.1 0.2-0.1 0.1-0.3 0-0.1 0-0.4-0.1-0.2 0-0.2 0-0.2 0.1-0.3 0.1-0.4 0.4-0.2 0.2-0.1 0.3-0.3 0.7-0.2 0.3-0.4 0.4-0.2 0.3-0.1 0.2 0 0.2-0.1 0.8-0.4 1.8 0 0.8 0 0.4-0.1 0.6-0.1 0.3 0 0.3-0.6 0.7-0.1 0.2 0 0.2 0.5 0.8 0.2 0.2 0.1 0.1 0.1 0.1 0.1 0.1 0.2 0 0.5 0 2.5-1.1 0.7-0.1 0.2 0.2 0.1 0.1 0.2 0.1 0.6 0.2 1.2 0.3 0.3 0.1 0.2 0.2 0.2 0.2 0.3 0.2 1.6 0.6 0.2 0 0.1 0.1 0.1 0.1 0.1 0.2 0 0.1 0.1 0.2 0.1 0.1 0.1 0 0.1 0.1 0.2 0.3 0.2 0.2 0.2 0.1 0.3 0.2 1.1 0.7 0.3 0.1 0.2 0 0.3-0.2 0.3-0.1 0.1 0.1 0.1 0.2 0 0.3 0.1 0.2 0.1 0.2 0.2 0.2 0.2 0 0.1 0 0.2-0.1 0.2-0.1 0.3 0 0.2 0.1 0.1 0.1 0.1 0.7 0 0.1 0.1 0.2 0.2 0.3 0.3 0.5 0.8 0.7 0.1 0.2 0.1 0.2 0 0.1 0 0.2-0.1 0.3 0.1 0.2 0 0.2 0.5 0.5 0.2 0.3 0.4 1 0.1 0.2 0.2 0.2 0.3 0.3 0.2 0.1 0.1-0.1 0.1-0.2-0.2-1 0.1-0.1 0-0.2 0.1-0.1 0.1-0.1 0.2-0.1 0.2-0.2 0.2-0.4 0.1-0.3 0.1-0.5 0-0.2 0-0.2 0-0.1-0.4-1.3-0.4-0.7 0-0.1-0.1-0.2 0-0.1 0-0.2 0-0.4 0.1-0.3 0-0.2 0-0.2 0-0.4-0.1-0.3-0.1-0.1-0.1-0.1-0.1-0.1-0.3-0.1-0.1-0.1-0.1-0.2-0.1-0.1 0-0.1-0.1-0.4 0-0.2 0-0.2 0.2-1 0.1-0.2 0.1-0.1 0.2-0.2 0.1-0.1 0.1-0.3 0.1-0.1 0.1-0.2 0-0.4 0-0.3-0.1-0.7 0-0.2-0.2-0.9 0-0.2 0.1 0 0.4 0.1 0.2 0.1 0.1 0.1 0.1 0.1 0.1 0.1 0.1 0.1 0.2 0 3.4 0.2 0.6 0.1 0.3 0.1 0.2 0.1-0.1 0.1-0.4 0.1-0.3 0.3-1 1.3-0.4 0.7-0.1 0.8 0.4 1.5 1.7 4.2-0.2 1.2-1-0.6-0.8 1.3-0.5 2.1-0.2 1.5 0.1 1.7-0.2 0.9-0.8 0-0.4 0.6-0.4 0.8-0.2 0.6 0 0.9 0.3 0.8 1.8 3.9 0.1 0.6 0.8 1.1 1.6 1.5 1.1 1.6-0.7 1.4 1.6-0.4-2.2 4.8-0.3 1.9 0.8 5.8 0.3 0.9 0.2 0.9-1 11.2 0.3 1.9 1.5 3.3 2.2 2.9 5.5 5.4-0.1 0.3-0.4 0.7-0.1 0.1-1 0.5-0.2 0.2 0 0.2 0.1 0.1 0.1 0.1 0.3 0.2 0.2 0.1 0.1 0.1 0.1 0.2 0.1 0.1 0.1 0.2 0 0.1 0 0.2-0.1 0.3-0.1 0.1-0.1 0.2-0.1 0.1-0.1 0.1-0.1 0.2-0.1 0.1 0 0.2 0.3 0.6 0 0.1-0.1 0.1-0.3 0.4-0.1 0.1-0.1 0.3 0 0.1 0 0.1 0 0.1 0.1 0.1 0.1 0.1 0.1 0.1 0.4 0.1 0.2 0 0.1 0.1 0.2 0.3 0.2 0.3 0.1 0.2 0 0.3 0.1 0.2-0.1 0.3 0 0.2 0 0.1-0.5 0.7-0.1 0.1-0.1 0.1 0 0.1 0.1 0.2 0 0.2-0.2 0.1-0.4-0.2-6.6-3.9-0.1-0.1-0.2-0.4-0.4-0.4-0.3-0.2-2.6-1-0.6-0.4-0.5-0.3-0.7 0-1.9 0.4-7 2.6-0.1 0-0.3-0.1-7.3-3-1.6-1.3-6.6-7.9-0.6-0.4-2.5-1-2-0.7-0.5-2.1-0.3-0.5-0.1 0-0.1 0.1-0.1 0.1-0.1 0.1-0.2 0-0.1 0-0.2-0.1-9.3-6.4-1.4-1.8-0.1-0.1-1.8-2-0.3-0.2-3.6-0.6-0.3 0-5.8-1.9-1.9-0.9-0.4-0.1-0.3-0.1 0 0.2-0.1 0.1-0.2 0-0.5 0.1-0.1 0.1-0.2 0.1-0.3 0.2-0.5 0.2-0.4 0.1-0.2 0-0.2-0.1 0-0.1-0.1-0.2 0-0.5 0-0.2 0-0.2-0.1-0.1-0.2-0.5-0.1-0.2-0.2-0.3-0.2-0.1-0.2 0-0.6 0-0.9 0-0.2-0.1-0.2 0-0.2-0.2-0.1-0.1-0.2-0.1-0.3-0.2-0.1-0.1-0.1-0.1 0-0.1 0-0.2-0.2-0.3-0.3-0.3-0.9-0.6-0.4-0.2-0.3-0.1-0.1 0.1 0 0.2-0.1 0.2 0 0.1-0.1 0.1-0.1 0.1-0.2 0.1-0.2 0.2-1.2-0.2-0.3 0-1.7-0.9-0.5-0.1-0.1-0.1-0.2 0-3-3.9 0.5-1.3 0.4-0.7 0.1-0.2 0-0.3-0.4-1.5 0-0.1-0.1-0.3-0.3-0.5-0.1-0.2-0.1-0.1 0-0.2 0-0.2 0-0.3 0.2-0.4 0.8-1.5 0.1-0.1 0.2-0.1 0.3-0.1 0.1-0.1 0.1-0.1 0.1-0.1 0.1-0.2 0.3-1.4-0.1-0.4-0.4-0.7-0.7-1-0.2-0.4 0-0.2 0-0.1-0.1-0.2-0.2-0.2-0.1-0.1-0.2-0.5-0.3-0.2-1.5-0.9-0.9-0.4-0.2 0-0.4 0-0.2 0-0.2-0.1-0.8-0.4-0.3-0.1-0.6-0.6-1.3-2-0.4-0.7-0.4-0.2-0.2 0-0.2-0.1-0.1-0.2 0-0.4 0.2-2 0-0.5-0.1-0.3-0.1-0.4-0.4-0.5-0.2-0.2-0.2-0.8-0.3-3.4-0.1-0.4-0.5-0.5 0-0.1 0-0.1 0.1-0.8 1-2.8-1.2-1.6-0.4-0.4-0.2-0.1-0.3-0.2-0.2-0.1-0.1 0.1-0.4 0.2-0.1 0-0.2-0.3-0.2-0.6-0.3-1.2 0-0.5 0-0.3 0.2-0.3 0-0.1 0-0.1-0.3-0.2-0.4-0.1-0.1-0.1-0.7-0.1-0.4-0.2-0.2-0.1-0.1-0.2 0-0.2 0.1-0.5 0-0.3 0.1-0.2 0-0.2 0-0.2-0.3-0.3-0.1-0.1-0.3-0.6-0.1-1.9-0.1-0.5-0.2-0.1-0.3-0.2-0.2-0.2-0.1-0.2 0.1-0.2 0.1-0.1 0.1-0.2 0.2-0.4 0-0.1 0.2-0.3 0-0.1 0-0.2-0.1-0.2-0.1-0.1-0.2-0.1-0.5-0.1-2.2-0.6-0.3 0.1-0.2-0.1-0.2-0.2-0.5-0.7-0.6-0.4 0.4-0.7 0.3-0.3 0.1-0.1 0.2-0.2 0.1-0.2 0.1-0.4 0.4-0.9 0.1-0.1 0.2-0.1 0.1 0 0.1-0.2 0-0.3-0.2-0.4-0.5-0.5-0.1-0.1 0-0.1-0.3-1 0-0.3 0.1-0.8 0.6-1.8z"
                    id="MY06"
                    name="Pahang"
                    fill="url(#MYGradient)"
                >
                </path>
                <path d="M185.1 135.6l0.2-0.7 0-0.3-0.4-1-0.1-0.2-0.2-0.2-0.2-0.3-0.1-0.4-0.1-0.7-0.1-0.2-0.1-0.2-0.4-0.1-0.2 0-2.3-1.7-1.1-0.5-0.2 0-0.1 0-0.2 0-0.1 0.1-0.1 0.1-0.1 0.1-0.3 0-0.5-0.2-0.2-0.2 0-0.2 0-0.9 0.1-0.4-0.1-0.7-0.4-1.3-0.1-0.4 0-0.2 0-0.1 0-0.1 0-0.1 0.1-0.2 0.1 0 0.2-0.1 0.1-0.1 0.1-0.1 0-0.3-0.5-2.2 0-0.3 0.1-0.1 0.1-0.1 0.3-0.1 0.2 0 0-0.2 0.1-0.1 0-0.2 0.1-0.1 0.2-0.3 0.1-0.2-0.1-0.1-0.4-0.3-0.4-0.5-0.2-0.1-0.1-0.1-0.2 0-0.2-0.2-0.2-0.1-0.6-0.8-0.2 0-0.3-0.2-0.2-0.2-0.2-0.1-0.2-0.1-0.2 0-0.2 0-0.1-0.1-0.2-0.2-0.2 0-0.6-0.2-0.3-0.1-0.1-0.2 0-0.2 0.3-0.8 0-0.3 0.1-0.2-0.1-0.2-0.1-0.7 0.1-2.7 0.2-1 0-0.2 0.6-1.2 0.2-1.1-0.1-0.5-0.2-0.2-1-0.8-0.8-1-0.1-0.2 0.1-0.2 0.1 0 0.1-0.5 0-0.7-0.3-2.8 0-0.4 0.4-0.3 0.1-0.1 0-0.2 0-2.5 0.1-0.8 0-0.2-0.2-0.5-0.1-0.3-0.6-2.6 0.2-0.6 1.6-1.7 3.6-3.3 1.7-2.1 0.1-0.1 0.4 0.3 2.4 1.5 1.1 0.9 0.9 1.1 0.6 1.5 0.5 0.7 1.2 0.6 8.3 6.6 2 0.9 1.9 0.4 0.8 0.5 2.4 2.2 4.3 4.8 4.7 7.5 1.6 3.9 4.6 7.7 2.7 5 0.3 0.9 0.2 0.3 0.8 1 0.2 0.4 0.1 0.6-0.4 1.3 0.1 1.1 0.4 1.9 0.8 7 0 1.1-0.5 0.9-0.3 0.9 0.6 2.1-0.1 0.7 1.5 4.3 0.1 1.4-0.1 0.5-0.7 1.6-0.3 0.6-0.3 0.2-0.7 0.8-0.2 0.4 0.1 0.4 0.3 1.2-0.3 0.5-0.3 0.1-0.2-0.1-0.3-0.1-0.6-0.1-3.4-0.2-0.2 0-0.1-0.1-0.1-0.1-0.1-0.1-0.1-0.1-0.2-0.1-0.4-0.1-0.1 0 0 0.2 0.2 0.9 0 0.2 0.1 0.7 0 0.3 0 0.4-0.1 0.2-0.1 0.1-0.1 0.3-0.1 0.1-0.2 0.2-0.1 0.1-0.1 0.2-0.2 1 0 0.2 0 0.2 0.1 0.4 0 0.1 0.1 0.1 0.1 0.2 0.1 0.1 0.3 0.1 0.1 0.1 0.1 0.1 0.1 0.1 0.1 0.3 0 0.4 0 0.2 0 0.2-0.1 0.3 0 0.4 0 0.2 0 0.1 0.1 0.2 0 0.1 0.4 0.7 0.4 1.3 0 0.1 0 0.2 0 0.2-0.1 0.5-0.1 0.3-0.2 0.4-0.2 0.2-0.2 0.1-0.1 0.1-0.1 0.1 0 0.2-0.1 0.1 0.2 1-0.1 0.2-0.1 0.1-0.2-0.1-0.3-0.3-0.2-0.2-0.1-0.2-0.4-1-0.2-0.3-0.5-0.5 0-0.2-0.1-0.2 0.1-0.3 0-0.2 0-0.1-0.1-0.2-0.1-0.2-0.8-0.7-0.3-0.5-0.2-0.3-0.1-0.2 0-0.1-0.1-0.7-0.1-0.1-0.2-0.1-0.3 0-0.2 0.1-0.2 0.1-0.1 0-0.2 0-0.2-0.2-0.1-0.2-0.1-0.2 0-0.3-0.1-0.2-0.1-0.1-0.3 0.1-0.3 0.2-0.2 0-0.3-0.1-1.1-0.7-0.3-0.2-0.2-0.1-0.2-0.2-0.2-0.3-0.1-0.1-0.1 0-0.1-0.1-0.1-0.2 0-0.1-0.1-0.2-0.1-0.1-0.1-0.1-0.2 0-1.6-0.6-0.3-0.2-0.2-0.2-0.2-0.2-0.3-0.1-1.2-0.3-0.6-0.2-0.2-0.1-0.1-0.1-0.2-0.2-0.7 0.1-2.5 1.1-0.5 0-0.2 0-0.1-0.1-0.1-0.1-0.1-0.1-0.2-0.2-0.5-0.8 0-0.2 0.1-0.2 0.6-0.7 0-0.3 0.1-0.3 0.1-0.6 0-0.4 0-0.8 0.4-1.8 0.1-0.8 0-0.2 0.1-0.2 0.2-0.3 0.4-0.4 0.2-0.3 0.3-0.7 0.1-0.3 0.2-0.2 0.4-0.4 0.3-0.1 0.2-0.1 0.2 0 0.2 0 0.4 0.1 0.1 0 0.3 0 0.1-0.1 0.1-0.2-0.3-0.7 0.1-0.3 0.1-0.4 0.3-0.6 0.3-0.7-0.1-0.6-0.3 0-0.1-0.2-0.1-0.3 0-0.5-0.1-0.2-0.1-0.2-1.6-1.5-0.2-0.1-0.2 0-0.1 0.1-0.1 0.1-0.1 0.1-0.1 0.1-0.1 0.4 0 0.1-0.1 0.1-0.2 0-0.1-0.1-0.3-0.2-0.1-0.1-0.2-0.1-0.1 0-0.6-0.1-0.2 0.1-0.3 0-0.2-0.1-0.1 0-0.6 0-0.2-0.1-0.2-0.2-0.3-0.5-0.3-0.3-0.4-0.2-0.2-0.3-0.1-0.4-0.2-0.8-0.1-0.4-0.1-0.4 0.1-0.4 0.1-0.2 0.2-0.1 0.1-0.1 0.5-0.2 0.1 0 0.1-0.1 0.1-0.2 0.1-0.1 0-0.2 0.1-1 0.1-0.2 0.1-0.1 0.1-0.2 0-0.3-0.2-0.5 0-0.3-0.2-0.4 0-0.1-0.5-0.5-0.8-0.8-0.7-0.2-1.3 0.1-0.8 0-0.9-0.1-0.2-0.1-0.3-0.1-0.9-0.2-0.5-0.3-0.8-0.6-0.7-0.4-0.4-0.5-1-1.9z"
                    id="MY11"
                    name="Terengganu"
                    fill="url(#MYGradient)"
                >
                </path>
                <path d="M768 107.8l0.5 2.4 0 1.1-0.9 0.9-0.4-0.1-0.9-0.5-0.4 0-0.9 0.8-1.2 0.7 0.1-0.7 1.3-3.6 0.4-0.5 1.3-1.2 0.7-0.3 0.4 1z"
                    id="MY15" name="Labuan" fill="url(#MYGradient)">
                </path>
                <path d="M141.4 208.1l0.3-0.1 0.2-0.1 0.5-0.2 0.2-0.1 0.1 0.1 0.1 0.2 0.2 0.2 0.2 0.5 0.2 0.4 0.1 0.2 0 0.3 0 0.2-0.1 0.2-0.3 0.2-0.1 0.2-0.1 0.7 0 0.2-0.2 0.2-0.1 0.1-0.1 0.3 0.1 0.5 0.2 0.9 0.2 1.8 0 0.3-0.1 0.1-0.1 0.2-0.2 0-0.1 0-0.2 0.1-0.1 0.1-0.1 0.3-0.1 0.2-0.1 0.1-0.1 0-0.1-0.2-0.1-0.2-0.1 0-0.1-0.1-0.3 0-1.3 0.1-0.3 0-0.3 0-0.2-0.1-0.2-0.1 0-0.2-0.1-0.3-0.1-0.3 0-0.2 0-0.2 0.1-0.1 0.3-0.4 0-0.1 0-0.1 0-0.2-0.1-0.3-0.1-0.1-0.1-0.1 0-0.1-0.1-0.1 0-0.2 0-0.2-0.1-0.2-0.2-0.2-0.6-0.3-0.1-0.1-0.1-0.2-0.1-0.1-0.2-0.3-0.1-0.2-0.1-1.6 0-0.2 0.6-1.1 0.2-0.3 0.1-0.1 0-0.1 0.6-0.3 0.3-0.2 0.1-0.1 0.1-0.1 0.1 0 0.3 0.1 0.5 0.6 1.1 0.5 0.4 0z"
                    id="MY10" name="Kuala Lumpur" fill="url(#MYGradient)">
                </path>
                <path d="M139.7 223.5l-0.2-0.1-0.2-0.1-0.2-0.4 0-0.3 0.1-0.4 0.2-0.2 0.1-0.2 0.1-0.1 0-0.3-0.2-0.7 0-0.3 0.2-0.1 0-0.3-0.2-0.2-0.1-0.3 0.1-0.3 0.3-0.1 0.7-0.1 0.2-0.2 0.2 0 0.4-0.3 0.1 0.9 0.4 0.3 0.3 0.5 0 0.8-0.9 1.1-0.2 0.4-0.2 0.3-0.6 0.6-0.4 0.1z"
                    id="MY10" name="Putrajaya" fill="url(#MYGradient)">
                </path>
                <style>{`
        #${activeState} {
            fill: url(#HighlightGradient) !important;
            stroke: #c8ffe4ff !important;
            stroke-width: 3px !important;
            filter: drop-shadow(0 0 15px rgba(255, 255, 255, 0.7));
            animation: highlightPulse 2s infinite;
        }
        
        @keyframes highlightPulse {
            0% { opacity: 0.8; }
            50% { opacity: 1; }
            100% { opacity: 0.8; }
        }
    `}</style>
            </g>

            <g id="points">
                <circle class="1.176590816028057|100.62687099273207" cx="90.9" cy="302.2" id="0">
                </circle>
                <circle class="3.7783548330590144|110.44330035742861" cx="545.5" cy="181.6" id="1">
                </circle>
                <circle class="7.030559854347713|118.29644384918585" cx="909.1" cy="30.3" id="2">
                </circle>
            </g>
            <g id="label_points">
                <circle class="Sabah" cx="839.8" cy="111.7" id="MY12">
                </circle>
                <circle class="Perlis" cx="73.4" cy="55.2" id="MY09">
                </circle>
                <circle class="Kedah" cx="93.7" cy="80.9" id="MY02">
                </circle>
                <circle class="Kelantan" cx="156.5" cy="118.5" id="MY03">
                </circle>
                <circle class="Perak" cx="108.6" cy="128.6" id="MY08">
                </circle>
                <circle class="Sarawak" cx="698.6" cy="239.9" id="MY13">
                </circle>
                <circle class="Pulau Pinang" cx="83.1" cy="106.1" id="MY07">
                </circle>
                <circle class="Selangor" cx="131.5" cy="198.2" id="MY10">
                </circle>
                <circle class="Negeri Sembilan" cx="164.6" cy="226.1" id="MY05">
                </circle>
                <circle class="Melaka" cx="170.3" cy="249.2" id="MY04">
                </circle>
                <circle class="Johor" cx="219.2" cy="262.2" id="MY01">
                </circle>
                <circle class="Pahang" cx="174.6" cy="183.5" id="MY06">
                </circle>
                <circle class="Terengganu" cx="196.7" cy="122" id="MY11">
                </circle>
                <circle class="Labuan" cx="766.6" cy="109.9" id="MY15">
                </circle>
                <circle class="Kuala Lumpur" cx="140.3" cy="210.9" id="MY14">
                </circle>
                <circle class="Putrajaya" cx="140.6" cy="220.8" id="MY16">
                </circle>
            </g>

            {/* Customer markers with state names */}
            <g id="customer-markers">
                {Object.entries(stateCenters).map(([stateId, pos]) => {
                    const count = customerByState[stateId] || 0;
                    if (count === 0) return null;

                    return (
                        <g key={stateId}>
                            {/* Customer count text - Add hover here */}
                            <style>
                                {`
                                @keyframes glow {
                                    0% {
                                        filter: drop-shadow(0 0 2px #ffc0c0ff)
                                                drop-shadow(0 0 5px  rgba(255, 88, 88, 1));
                                        opacity: 0.9;
                                         transform: translateY(0);
                                    }
                                    100% {
                                        filter: drop-shadow(0 0 4px #ffc0c0ff)
                                                drop-shadow(0 0 10px rgba(255, 88, 88, 1));
                                        opacity: 1;
                                         transform: translateY(-1px);
                                    }
                                }
                                     @keyframes bounce {
                                    0% {
                                         transform: translateY(0);
                                    
                                    25% {
                                         transform: translateY(-0.25px);
                                    }
                                    50% {
                                         transform: translateY(-0.5px);
                                    }
                                    75% {
                                         transform: translateY(-0.75px);
                                    }
                                    100% {
                                         transform: translateY(-1px);
                                    }
                                }
                                `}

                            </style>
                            <text
                                x={pos.x}
                                y={pos.y}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize="12"
                                fontWeight="bold"
                                fill={activeState === stateId ? '#ffffffff' : '#ff5050ff'}
                                stroke={activeState === stateId ? '#326c92ff' : '#ff9191ff'}
                                style={{
                                    animation: 'glow 5s ease-in-out infinite',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    pointerEvents: 'auto',
                                }}
                                onMouseEnter={() => {
                                    if (activeState !== stateId) {
                                        setActiveState(stateId);
                                    }
                                }}
                                onMouseLeave={() => {
                                    // Don't close on mouse leave if we're not hovering
                                    // Let it stay open for click
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    // Toggle: if same state, close it
                                    if (activeState === stateId) {
                                        setActiveState(null);
                                    } else {
                                        setActiveState(stateId);
                                    }
                                }}
                            >
                                ▼
                            </text>

                        </g>
                    );
                })}
            </g>

        </svg>
    );

    // Calculate valid states (with users > 0)
    const validStates = useMemo(() => {
        return STATE_ORDER.filter(state => {
            const count = customerByState[state];
            return count !== undefined && count > 0;
        });
    }, []);
    // Calculate current position for display
    const autoDisplay = useMemo(() => {
        if (loading) {
            return 'Loading...';
        }

        if (!plantData?.data || plantData.data.length === 0) {
            return 'No data';
        }

        if (!activeState || validStates.length === 0) {
            return '0/0';
        }

        const currentIndex = validStates.indexOf(activeState);

        // Show loop progress if auto-loop is active
        if (isAutoLoopActive) {
            return `${currentIndex + 1}/${validStates.length} (Auto)`;
        }

        return `${currentIndex + 1}/${validStates.length}`;
    }, [activeState, validStates, loading, plantData, isAutoLoopActive]);

    // Reset loop when component mounts or when returning from BigScreen
    useEffect(() => {
        // Reset loop states
        setIsAutoLoopActive(false);
        setLoopCompleted(false);
        setVisitedStates(new Set());

        // Clear any existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
    }, []); // Empty dependency array - runs once on mount

    //navigate to big screen based on selected client
    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleClientSelect = (client) => {
        handleMenuClose();
        navigate("/BigScreen", {
            state: {
                selectedClient: client,
                // You can send additional data if needed
                fromMap: true,
                timestamp: new Date().toISOString()
            }
        });
    };




    return (
        <Box
            sx={{
                width: "100%",
                height: "100vh",
                display: "flex",
                alignItems: "left",
                flexDirection: 'column',
                backgroundImage: `url(${bg1})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
            }}>
            <ToastContainer />

            {/* ----------------button top right button--------------------- */}
            <Box sx={{
                width: "auto",
                height: "auto",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "0.5vw",         // 8px → 0.5vw
                zIndex: 1100,
                position: "absolute",
                top: "0.5vw",         // 8px → 0.5vw
                right: "0.5vw",       // 8px → 0.5vw
            }}>

                {/* Auto Play Button Container */}
                <IconButton
                    sx={{
                        color: isAutoLoopActive ? '#4CAF50' : 'rgb(188, 253, 255)',
                        padding: "0.5vw",   // 8px → 0.5vw
                        "&:hover": {
                            color: isAutoLoopActive ? '#45a049' : colors.bs3,
                            transform: "scale(1.1)"
                        }
                    }}
                    onClick={() => {
                        setShowAutoControl(!showAutoControl);
                        if (controlPanelTimer) {
                            clearTimeout(controlPanelTimer);
                            setControlPanelTimer(null);
                        }
                    }}
                >
                    <PlayCircleIcon
                        sx={{
                            fontSize: "1.5vw",
                            transition: "transform 0.3s ease-in-out, color 0.3s ease-in-out",
                        }}
                    />
                </IconButton>

                {/* Control Panel */}
                {showAutoControl && (
                    <Box
                        ref={controlPanelRef}
                        sx={{
                            position: "absolute",
                            top: "100%",
                            right: "0",
                            width: "18vw",          // 280px → 18vw
                            maxHeight: "26vw",      // 400px → 26vw
                            overflowY: "auto",
                            borderRadius: "1.3vw",  // 20px → 1.3vw
                            zIndex: 2000,
                            p: "1.5vw",            // p:3 → 1.5vw
                            animation: "fadeIn 0.3s ease-in-out",
                            background: "rgba(0, 0, 0, 0.7)",
                            backdropFilter: "blur(20px)",
                            WebkitBackdropFilter: "blur(20px)",
                            borderBottom: "2px solid rgba(96, 166, 194, 0.38)",
                            borderRight: "2px solid rgba(96, 166, 194, 0.21)",
                            boxShadow: "4px 4px 20px rgba(255, 255, 255, 0.2)",
                            backgroundImage: "linear-gradient(135deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.03))",
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: '-0.65vw',     // -10px → -0.65vw
                                right: '1.3vw',     // 20px → 1.3vw
                                borderLeft: '0.65vw solid transparent',
                                borderRight: '0.65vw solid transparent',
                                borderBottom: '0.65vw solid rgba(96, 166, 194, 0.38)',
                            },
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: '-0.52vw',     // -8px → -0.52vw
                                right: '1.3vw',     // 20px → 1.3vw
                                borderLeft: '0.65vw solid transparent',
                                borderRight: '0.65vw solid transparent',
                                borderBottom: '0.65vw solid rgba(0, 0, 0, 0.7)',
                                backdropFilter: "blur(20px)",
                            }
                        }}
                    >
                        {/* Header */}
                        <Box sx={{
                            mb: "1.5vw",            // mb:3 → 1.5vw
                            position: 'relative',
                            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                            pb: "1vw",              // pb:2 → 1vw
                        }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: "bold",
                                    color: "white",
                                    textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                    textAlign: "left",
                                    fontSize: "1vw"     // 16px → 1vw
                                }}
                            >
                                Auto Play Settings
                            </Typography>

                            <Box sx={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5vw"            // gap:1 → 0.5vw
                            }}>
                                <Typography
                                    sx={{
                                        fontSize: "0.75vw",     // 12px → 0.75vw
                                        color: isAutoLoopActive ? "#4CAF50" : "#ff0000ff",
                                        fontWeight: "bold",
                                        backgroundColor: "rgba(0,0,0,0.3)",
                                        px: "0.5vw",
                                        py: "0.25vw",
                                        borderRadius: "0.25vw"  // 4px → 0.25vw
                                    }}
                                >
                                    {isAutoLoopActive ? "● ON" : "● OFF"}
                                </Typography>
                                <ClearIcon
                                    sx={{
                                        fontSize: "1.3vw",      // 20px → 1.3vw
                                        color: "#ff9191",
                                        cursor: "pointer",
                                        backgroundColor: "rgba(0,0,0,0.3)",
                                        borderRadius: "50%",
                                        p: "0.25vw",
                                        "&:hover": {
                                            color: "#ff5050",
                                            backgroundColor: "rgba(0,0,0,0.5)"
                                        }
                                    }}
                                    onClick={() => {
                                        setShowAutoControl(false);
                                        if (controlPanelTimer) {
                                            clearTimeout(controlPanelTimer);
                                        }
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Quick Settings */}
                        <Box sx={{
                            mb: "1.5vw",                // mb:3 → 1.5vw
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            borderRadius: "0.75vw",     // 12px → 0.75vw
                            p: "1vw",                   // p:2 → 1vw
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                        }}>
                            <Typography sx={{
                                color: "rgba(255,255,255,0.9)",
                                mb: "1vw",              // mb:2 → 1vw
                                fontSize: "0.9vw",      // 14px → 0.9vw
                                fontWeight: "500"
                            }}>
                                Speed Settings:
                            </Typography>
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5vw" }}>  {/* gap:1 → 0.5vw */}
                                {[10, 20, 30, 45].map((speed) => (
                                    <Box
                                        key={speed}
                                        sx={{
                                            py: "0.6vw",        // py:1.2 → 0.6vw
                                            textAlign: "center",
                                            backgroundColor: autoPlaySpeed === speed
                                                ? "rgba(96, 166, 194, 0.3)"
                                                : "rgba(255,255,255,0.07)",
                                            borderRadius: "0.5vw",  // 8px → 0.5vw
                                            border: autoPlaySpeed === speed
                                                ? "1px solid rgba(96, 166, 194, 0.7)"
                                                : "1px solid rgba(255,255,255,0.1)",
                                            cursor: "pointer",
                                            transition: "all 0.2s",
                                            "&:hover": {
                                                backgroundColor: autoPlaySpeed === speed
                                                    ? "rgba(96, 166, 194, 0.4)"
                                                    : "rgba(255,255,255,0.12)",
                                                transform: "translateY(-1px)",
                                            }
                                        }}
                                        onClick={() => setAutoPlaySpeed(speed)}
                                    >
                                        <Typography sx={{
                                            color: "white",
                                            fontSize: "0.85vw",     // 13px → 0.85vw
                                            fontWeight: autoPlaySpeed === speed ? "bold" : "normal"
                                        }}>
                                            {speed}s
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Start/Stop Button */}
                        <Box sx={{
                            display: "flex",
                            gap: "1vw",             // gap:2 → 1vw
                            mt: "1vw"               // mt:2 → 1vw
                        }}>
                            <Box
                                sx={{
                                    flex: 1,
                                    py: "0.9vw",        // py:1.8 → 0.9vw
                                    textAlign: "center",
                                    backgroundColor: isAutoLoopActive
                                        ? "rgba(244, 67, 54, 0.15)"
                                        : "rgba(76, 175, 80, 0.15)",
                                    borderRadius: "0.75vw",     // 12px → 0.75vw
                                    border: isAutoLoopActive
                                        ? "1px solid rgba(244, 67, 54, 0.5)"
                                        : "1px solid rgba(76, 175, 80, 0.5)",
                                    cursor: "pointer",
                                    transition: "all 0.3s",
                                    boxShadow: isAutoLoopActive
                                        ? "0 4px 12px rgba(244, 67, 54, 0.3)"
                                        : "0 4px 12px rgba(76, 175, 80, 0.3)",
                                    "&:hover": {
                                        backgroundColor: isAutoLoopActive
                                            ? "rgba(244, 67, 54, 0.25)"
                                            : "rgba(76, 175, 80, 0.25)",
                                        transform: "translateY(-2px)",
                                        boxShadow: isAutoLoopActive
                                            ? "0 6px 16px rgba(244, 67, 54, 0.4)"
                                            : "0 6px 16px rgba(76, 175, 80, 0.4)",
                                    }
                                }}
                                onClick={() => {
                                    if (isAutoLoopActive) {
                                        setIsAutoLoopActive(false);
                                        if (intervalRef.current) {
                                            clearInterval(intervalRef.current);
                                        }
                                        setShowAutoControl(false);
                                    } else {
                                        setIsAutoLoopActive(true);
                                        setLoopCompleted(false);
                                        setVisitedStates(new Set());
                                        setIsAutoRotationPaused(false);
                                        setShowAutoControl(false);
                                    }
                                    if (controlPanelTimer) {
                                        clearTimeout(controlPanelTimer);
                                    }
                                }}
                            >
                                <Typography sx={{
                                    color: isAutoLoopActive ? "#f44336" : "#4CAF50",
                                    fontWeight: "bold",
                                    fontSize: "0.95vw",     // 15px → 0.95vw
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "0.5vw",
                                    textShadow: "0 1px 2px rgba(0,0,0,0.3)"
                                }}>
                                    {isAutoLoopActive ? (
                                        <>
                                            <span style={{ fontSize: "1.3vw" }}>■</span> Stop Auto  {/* 20px → 1.3vw */}
                                        </>
                                    ) : (
                                        <>
                                            <span style={{ fontSize: "1.3vw" }}>▶</span> Start Auto  {/* 20px → 1.3vw */}
                                        </>
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* BigScreen Button */}
                <IconButton
                    onClick={handleMenuClick}
                    sx={{
                        color: 'rgb(188, 253, 255)',
                        padding: "0.5vw",   // 8px → 0.5vw
                        "&:hover": {
                            color: colors.bs3,
                            transform: "scale(1.1)"
                        }
                    }}
                >
                    <ExpandCircleDownIcon sx={{ fontSize: "1.5vw" }} />
                </IconButton>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    PaperProps={{
                        sx: {
                            background: "rgba(0, 0, 0, 0.7)",
                            backdropFilter: "blur(20px)",
                            WebkitBackdropFilter: "blur(20px)",
                            borderBottom: "2px solid rgba(96, 166, 194, 0.38)",
                            borderRight: "2px solid rgba(96, 166, 194, 0.21)",
                            boxShadow: "4px 4px 20px rgba(255, 255, 255, 0.2)",
                            backgroundImage: "linear-gradient(135deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.03))",
                            maxHeight: '26vw',      // 400px → 26vw
                            overflowY: 'auto',
                            minWidth: '16vw',       // 250px → 16vw
                            borderRadius: '1.3vw',  // 20px → 1.3vw
                            mt: 1,
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: '-0.65vw',
                                right: '1.3vw',
                                borderLeft: '0.65vw solid transparent',
                                borderRight: '0.65vw solid transparent',
                                borderBottom: '0.65vw solid rgba(96, 166, 194, 0.38)',
                            },
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: '-0.52vw',
                                right: '1.3vw',
                                borderLeft: '0.65vw solid transparent',
                                borderRight: '0.65vw solid transparent',
                                borderBottom: '0.65vw solid rgba(0, 0, 0, 0.7)',
                                backdropFilter: "blur(20px)",
                            },
                            '&::-webkit-scrollbar': {
                                width: '0.5vw',     // 8px → 0.5vw
                            },
                            '&::-webkit-scrollbar-track': {
                                background: 'rgba(0, 0, 0, 0.2)',
                                borderRadius: '0.25vw',
                                margin: '0.25vw 0',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'rgba(96, 166, 194, 0.5)',
                                borderRadius: '0.25vw',
                                border: '2px solid transparent',
                                backgroundClip: 'padding-box',
                                '&:hover': {
                                    background: 'rgba(96, 166, 194, 0.7)',
                                }
                            },
                            '&::-webkit-scrollbar-thumb:active': {
                                background: 'rgba(96, 166, 194, 0.9)',
                            }
                        }
                    }}
                >
                    {clientsArray.length === 0 ? (
                        <MenuItem disabled sx={{ fontSize: "0.9vw", color: "rgba(255, 255, 255, 0.6)", textAlign: "center", py: "1vw" }}>
                            No clients available
                        </MenuItem>
                    ) : (
                        clientsArray.map((client, index, array) => (
                            <MenuItem
                                key={client.client_id}
                                onClick={() => handleClientSelect(client)}
                                sx={{
                                    fontSize: "0.9vw",      // 14px → 0.9vw
                                    color: "rgba(255, 255, 255, 0.9)",
                                    backgroundColor: "transparent",
                                    paddingY: "0.75vw",     // 12px → 0.75vw
                                    paddingX: "1vw",
                                    minHeight: "unset",
                                    borderBottom: index === array.length - 1 ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        backgroundColor: "rgba(96, 166, 194, 0.15)",
                                        color: "rgb(188, 253, 255)",
                                        transform: "translateX(4px)"
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <Box sx={{
                                        width: '0.5vw',         // 8px → 0.5vw
                                        height: '0.5vw',        // 8px → 0.5vw
                                        borderRadius: '50%',
                                        backgroundColor: 'rgba(96, 166, 194, 0.8)',
                                        marginRight: '0.75vw',  // 12px → 0.75vw
                                        boxShadow: '0 0 8px rgba(96, 166, 194, 0.5)'
                                    }} />
                                    <Typography sx={{ fontSize: "0.9vw", fontWeight: 500 }}>  {/* 14px → 0.9vw */}
                                        {client.company_name}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))
                    )}
                </Menu>

                {/* PlantList Button */}
                <IconButton
                    onClick={() => navigate("/PlantList")}
                    sx={{
                        color: 'rgb(188, 253, 255)',
                        padding: "0.5vw",   // 8px → 0.5vw
                        "&:hover": {
                            color: colors.bs3,
                            transform: "scale(1.1)"
                        }
                    }}
                >
                    <ClearIcon sx={{ fontSize: "1.5vw" }} />
                </IconButton>
            </Box>
            {/* ----------------logo--------------------- */}
            <Box sx={{
                width: "10%",
                height: "10%",
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: "1%",
                zIndex: 1,
                position: "absolute",
                top: "2%",
                left: "45%",
            }}>
                {userDetail?.logo && (
                    <img
                        src={`data:image/jpeg;base64,${userDetail.logo}`}
                        alt="eCos logo"
                        style={{
                            height: '100%',
                            width: 'auto',
                            maxWidth: '100%',
                            objectFit: 'contain'
                        }}
                    />
                )}
            </Box>

            {/* ----------------Malaysia Map Section--------------------- */}
            <Box
                ref={containerRef}
                sx={{
                    width: "100%",
                    height: "70%",
                    alignItems: "center",
                    justifyContent: "center",
                    display: "flex",
                    position: "relative",
                }}
                data-container="true"
            >
                <MalaysiaMap />

                {/* Active state popup */}
                {isPositionsReady && activeState && customers[activeState] && statePositions[activeState] && (
                    <Box
                        data-popup="true"
                        sx={{
                            position: "absolute",
                            top: `${getPopupPosition(activeState).y}px`,
                            left: `${getPopupPosition(activeState).x}px`,
                            width: "23.5vw",
                            maxHeight: "60vh",
                            overflowY: "auto",
                            borderRadius: "1.3vw",
                            zIndex: 1000,

                            background: "rgba(0, 0, 0, 0.7)",
                            backdropFilter: "blur(20px)",
                            WebkitBackdropFilter: "blur(20px)",
                            borderBottom: "2px solid rgba(96, 166, 194, 0.38)",
                            borderRight: "2px solid rgba(96, 166, 194, 0.21)",
                            boxShadow: "4px 4px 20px rgba(255, 255, 255, 0.2)",
                            backgroundImage: "linear-gradient(135deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.03))",
                            p: "1.5vw",

                            '&::-webkit-scrollbar': { width: '0.4vw' },
                            '&::-webkit-scrollbar-track': {
                                background: 'rgba(0, 0, 0, 0.2)',
                                borderRadius: '0.4vw',
                                margin: '0.25vw 0',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'rgba(96, 166, 194, 0.5)',
                                borderRadius: '0.25vw',
                                border: '2px solid transparent',
                                backgroundClip: 'padding-box',
                                '&:hover': { background: 'rgba(96, 166, 194, 0.7)' }
                            },
                            '&::-webkit-scrollbar-thumb:active': {
                                background: 'rgba(96, 166, 194, 0.9)',
                            }
                        }}
                    >
                        {/* Header */}
                        <Box sx={{ mb: "1.2vw", position: 'relative' }}>
                            <Typography sx={{
                                fontWeight: "bold",
                                color: "white",
                                textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                textAlign: "center",
                                fontSize: "1.1vw",
                            }}>
                                {getStateName(activeState)}
                            </Typography>

                            <Typography sx={{
                                color: "rgba(255,255,255,0.8)",
                                textAlign: "center",
                                mt: "0.3vw",
                                fontSize: "0.75vw",
                            }}>
                                {customers[activeState].length} customer{customers[activeState].length > 1 ? 's' : ''}
                            </Typography>

                            {/* Close button */}
                            <Typography
                                onClick={() => setActiveState(null)}
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': { color: '#ff5050' },
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    color: 'rgba(96, 166, 194, 0.8)',
                                    backgroundColor: 'rgba(0,0,0,0.3)',
                                    px: "0.4vw",
                                    py: "0.2vw",
                                    fontSize: '1.2vw',
                                    lineHeight: 1,
                                    borderRadius: "0.3vw",
                                }}
                            >
                                ×
                            </Typography>
                        </Box>

                        {/* Table Container */}
                        <Box sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            borderRadius: "0.75vw",
                            overflow: "hidden",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                        }}>
                            <table style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                fontSize: "0.8vw",
                                color: "white",
                            }}>
                                <thead>
                                    <tr style={{
                                        backgroundColor: "rgba(1, 72, 102, 0.3)",
                                        backdropFilter: "blur(5px)",
                                    }}>
                                        <th style={{
                                            padding: "0.8vw 0.6vw",
                                            textAlign: "left",
                                            fontWeight: "600",
                                            fontSize: "0.85vw",
                                            borderBottom: "2px solid rgba(255, 255, 255, 0.1)",
                                            width: "25%",
                                        }}>User</th>
                                        <th style={{
                                            padding: "0.8vw 0.6vw",
                                            textAlign: "center",
                                            fontWeight: "600",
                                            fontSize: "0.85vw",
                                            borderBottom: "2px solid rgba(255, 255, 255, 0.1)",
                                            width: "45%",
                                        }}>Solar System Size</th>
                                        <th style={{
                                            padding: "0.8vw 0.6vw",
                                            textAlign: "center",
                                            fontWeight: "600",
                                            fontSize: "0.85vw",
                                            borderBottom: "2px solid rgba(255, 255, 255, 0.1)",
                                            width: "15%",
                                        }}>Solar</th>
                                        <th style={{
                                            padding: "0.8vw 0.6vw",
                                            textAlign: "center",
                                            fontWeight: "600",
                                            fontSize: "0.85vw",
                                            borderBottom: "2px solid rgba(255, 255, 255, 0.1)",
                                            width: "15%",
                                        }}>BESS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {customers[activeState].map((customer, index) => (
                                        <tr
                                            key={customer.name}
                                            style={{
                                                backgroundColor: index % 2 === 0
                                                    ? "rgba(255, 255, 255, 0.03)"
                                                    : "rgba(255, 255, 255, 0.07)",
                                                transition: "background-color 0.2s ease",
                                            }}
                                        >
                                            <td style={{
                                                padding: "0.7vw 0.6vw",
                                                borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                                                fontWeight: "500",
                                                fontSize: "0.8vw",
                                            }}>
                                                {customer.name}
                                            </td>
                                            <td style={{
                                                padding: "0.7vw 0.6vw",
                                                borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                                                color: "rgba(255, 255, 255, 0.9)",
                                                fontSize: "0.8vw",
                                                textAlign: "center",
                                            }}>
                                                {customer.consumption}
                                            </td>
                                            <td style={{
                                                padding: "0.7vw 0.6vw",
                                                borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                                                textAlign: "center",
                                            }}>
                                                <Box sx={{
                                                    width: "0.9vw",
                                                    height: "0.9vw",
                                                    borderRadius: "50%",
                                                    backgroundColor: customer.hasSolar ? "#4CAF50" : "#f44336",
                                                    display: "inline-block",
                                                    boxShadow: customer.hasSolar
                                                        ? "0 0 8px rgba(76, 175, 80, 0.5)"
                                                        : "0 0 8px rgba(244, 67, 54, 0.3)",
                                                }} />
                                            </td>
                                            <td style={{
                                                padding: "0.7vw 0.6vw",
                                                borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                                                textAlign: "center",
                                            }}>
                                                <Box sx={{
                                                    width: "0.9vw",
                                                    height: "0.9vw",
                                                    borderRadius: "50%",
                                                    backgroundColor: customer.hasBess ? "#4CAF50" : "#f44336",
                                                    display: "inline-block",
                                                    boxShadow: customer.hasBess
                                                        ? "0 0 8px rgba(76, 175, 80, 0.5)"
                                                        : "0 0 8px rgba(244, 67, 54, 0.3)",
                                                }} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Box>

                        {/* Legend */}
                        <Box sx={{
                            mt: "1.2vw",
                            pt: "0.8vw",
                            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: "0.5vw" }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: "0.3vw" }}>
                                    <Box sx={{
                                        width: "0.6vw",
                                        height: "0.6vw",
                                        borderRadius: "50%",
                                        bgcolor: "#4CAF50",
                                        boxShadow: "0 0 6px rgba(76, 175, 80, 0.5)",
                                    }} />
                                    <Typography sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "0.7vw" }}>
                                        With
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: "0.3vw", ml: "0.8vw" }}>
                                    <Box sx={{
                                        width: "0.6vw",
                                        height: "0.6vw",
                                        borderRadius: "50%",
                                        bgcolor: "#f44336",
                                        boxShadow: "0 0 6px rgba(244, 67, 54, 0.3)",
                                    }} />
                                    <Typography sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "0.7vw" }}>
                                        Without
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )}

            </Box>

            {/* ----------------Section 1: Metrics--------------------- */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                flexWrap: 'nowrap',
                padding: '0',
                alignItems: 'center',
                height: '20vh',
                mt: "2%",
                pb: 0,
            }}>
                {metrics.map((metric, index) => (
                    <Box
                        key={index}
                        sx={{
                            flex: "1%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            textAlign: "center",
                            height: "100%",
                            borderRight: index === metrics.length - 1 ? 'none' : '0.15vw solid rgba(255, 255, 255, 0.27)',
                            px: "2%",
                            position: 'relative',
                        }}
                    >
                        {/* Icon row - centered */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '30%',
                            width: '100%',
                        }}>
                            {metric.icon}
                        </Box>

                        {/* Title row - centered */}
                        <Typography sx={{
                            fontSize: "1.2vw",
                            fontWeight: "bold",
                            color: colors.f2,
                            height: '20%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            textAlign: 'center',
                        }}>
                            {metric.title}
                        </Typography>

                        {/* Value row - centered */}
                        <Typography sx={{
                            fontSize: "1.8vw",
                            fontWeight: 700,
                            color: colors.f2,
                            height: '30%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                        }}>
                            {metric.value} <span style={{ fontSize: "1.2vw", marginLeft: '1%' }}>{metric.unit}</span>
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Add CSS animation for the active indicator */}
            <style jsx="true">{`
                @keyframes pulse {
                    0% { opacity: 0.4; }
                    50% { opacity: 1; }
                    100% { opacity: 0.4; }
                }
            `}</style>
        </Box>
    );
};

export default Map;